<template>
  <div class="role-page d-flex" :class="{ 'role-page-exam-full': activeExam }">
    <aside v-if="!activeExam" class="side-nav">
      <h4 class="mb-3">学员中心</h4>
      <button class="nav-btn" :class="{ active: currentTab === 'home' }" @click="currentTab = 'home'">首页</button>
      <button class="nav-btn" :class="{ active: currentTab === 'exam' }" @click="currentTab = 'exam'">在线考试</button>
      <button class="nav-btn" :class="{ active: currentTab === 'score' }" @click="openScoreTab">成绩查询</button>
      <button type="button" class="nav-btn" @click="goMoreInfo">更多讯息</button>
      <button class="btn btn-success mt-auto w-100" @click="logout">退出登录</button>
    </aside>

    <main class="content-area" :class="{ 'content-area-exam-full': activeExam }">
      <section class="card-box" v-if="currentTab === 'home'">
        <h2>学员端首页</h2>
        <p>欢迎，{{ user.username || '学员' }}</p>
      </section>
      <section ref="noticeBoard" class="card-box notice-board-card" v-if="currentTab === 'home'">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0 notice-board-heading">
            <span class="notice-board-title-text">公告栏</span>
            <span
              v-if="hasUnreadNotice"
              class="notice-board-dot"
              title="有未读公告"
              aria-label="有未读公告"
            ></span>
          </h5>
          <button class="btn btn-light btn-sm" @click="loadNotices">刷新</button>
        </div>
        <div class="notice-board-list">
          <div v-for="n in notices" :key="'notice-' + n.id" class="notice-item notice-clickable" @click="openNoticeModal(n)">
            <div class="notice-title-row">
              <span v-if="isNoticeUnread(n)" class="notice-item-unread-dot" title="未读" aria-hidden="true"></span>
              <div class="notice-title">{{ n.title || '未命名公告' }}</div>
            </div>
            <div class="notice-meta">发布人：{{ n.publisher || '管理员' }} · {{ $fmtDateTime(n.publishTime || n.createTime) }}</div>
            <div class="notice-content">{{ noticePreviewText(n) }}</div>
          </div>
          <div v-if="notices.length === 0" class="text-muted">暂无公告</div>
        </div>
      </section>
      <section class="card-box exam-entry-strong" v-if="currentTab === 'home'">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">我的考试信息</h5>
          <button class="btn btn-light btn-sm" @click="loadExamData">刷新</button>
        </div>
        <div class="result-shell">
          <table class="table table-striped align-middle modern-table">
            <thead>
              <tr>
                <th>考试名称</th>
                <th>开始时间</th>
                <th>结束时间</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in myExamInfos" :key="'home-exam-' + item.recordId">
                <td>{{ item.examName || '-' }}</td>
                <td>{{ $fmtDateTime(item.startTime) }}</td>
                <td>{{ $fmtDateTime(item.endTime) }}</td>
                <td>{{ displayExamStatusLabel(item.exam) }}</td>
                <td>
                  <button class="btn btn-sm btn-primary" @click="enterExamFromHome(item)">进入考试</button>
                </td>
              </tr>
              <tr v-if="myExamInfos.length === 0">
                <td colspan="5" class="text-muted">暂无考试信息</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="card-box exam-entry-strong" v-if="currentTab === 'score'">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">成绩查询</h4>
          <button class="btn btn-light btn-sm" @click="loadMyScores">刷新</button>
        </div>
        <div class="small mb-2 text-muted">仅显示管理员已公开成绩的考试。</div>
        <div class="result-shell">
          <table class="table table-striped align-middle modern-table">
            <thead>
              <tr>
                <th>考试ID</th>
                <th>考试名称</th>
                <th>分数</th>
                <th>交卷时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in scoreRows" :key="'score-' + row.recordId">
                <td>{{ row.examId }}</td>
                <td>{{ row.examName || '-' }}</td>
                <td>{{ row.score ?? 0 }}</td>
                <td>{{ $fmtDateTime(row.submitTime) }}</td>
                <td class="d-flex flex-wrap gap-2 align-items-center">
                  <button
                    v-if="Number(row.showAnswerAfterSubmit) === 1 && isSubmittedExamStatus(row.status)"
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click="openAnswerReview(row.recordId)"
                  >
                    查看答案
                  </button>
                  <button
                    v-if="Number(row.allowRedo) === 1 && isSubmittedExamStatus(row.status)"
                    type="button"
                    class="btn btn-sm btn-outline-warning"
                    @click="redoExam(row.recordId)"
                  >
                    重做试卷
                  </button>
                  <span
                    v-if="Number(row.showAnswerAfterSubmit) === 1 && !isSubmittedExamStatus(row.status)"
                    class="text-muted small"
                  >未交卷</span>
                  <span
                    v-else-if="Number(row.showAnswerAfterSubmit) !== 1 && Number(row.allowRedo) !== 1"
                    class="text-muted small"
                  >—</span>
                </td>
              </tr>
              <tr v-if="scoreRows.length === 0">
                <td colspan="5" class="text-muted">暂无可查看成绩（可能管理员尚未公开）</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="card-box exam-entry-strong" v-if="currentTab === 'answerReview'">
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 class="mb-0">答卷解析：{{ answerReview.examName || '本场考试' }}</h4>
          <div class="d-flex gap-2">
            <button
              v-if="Number(answerReview.allowRedo) === 1"
              type="button"
              class="btn btn-warning btn-sm"
              @click="redoExam(answerReview.recordId)"
            >
              重做试卷
            </button>
            <button type="button" class="btn btn-light btn-sm" @click="closeAnswerReview">返回</button>
          </div>
        </div>
        <div v-if="answerReview.loading" class="text-muted">正在加载答案与解析…</div>
        <div v-else-if="!answerReview.showAnswerAfterSubmit" class="text-muted">
          {{ answerReview.message || '本场考试未开放提交后查看答案' }}
        </div>
        <div v-else class="answer-review-list">
          <div
            v-for="(q, idx) in answerReview.questions"
            :key="'rev-' + (q.questionId || idx)"
            class="answer-review-item mb-4"
          >
            <div class="answer-review-qhead mb-2">
              <strong>第 {{ idx + 1 }} 题</strong>
              <span class="ms-2 text-muted small">{{ q.typeText || examQuestionTypeLabel(q.type) }}</span>
              <span v-if="q.score != null" class="ms-2 text-muted small">分值 {{ q.score }}</span>
            </div>
            <div class="answer-review-title mb-2">{{ q.title }}</div>
            <div v-if="stemImageList(q).length" class="mb-2 d-flex flex-wrap gap-2">
              <img
                v-for="(u, ci) in stemImageList(q)"
                :key="'rev-ci-' + q.questionId + '-' + ci"
                :src="u"
                class="exam-q-img-thumb"
                alt=""
                loading="lazy"
                @click="openImageLightbox(u)"
              />
            </div>

            <div v-if="isChoiceReviewType(q.type)" class="exam-choice-list mb-2">
              <div
                v-for="opt in reviewOptionEntries(q)"
                :key="'rev-opt-' + q.questionId + '-' + opt.key"
                class="exam-choice-btn exam-choice-btn--rich review-opt"
                :class="reviewOptionMarkClass(q, opt.key)"
              >
                <span class="exam-choice-main">
                  <span class="exam-choice-key">{{ opt.key }}.</span>
                  <span class="exam-choice-text">{{ opt.value }}</span>
                </span>
                <span v-if="opt.imageUrl" class="exam-choice-img">
                  <img
                    :src="opt.imageUrl"
                    class="exam-q-img-thumb"
                    alt=""
                    loading="lazy"
                    @click="openImageLightbox(opt.imageUrl)"
                  />
                </span>
              </div>
            </div>
            <div v-else class="mb-2">
              <div class="small text-muted mb-1">你的作答</div>
              <pre class="answer-review-user-ans mb-0">{{ displayReviewUserAnswer(q) }}</pre>
            </div>

            <div class="answer-review-correct mb-2">
              <div class="small text-muted mb-1">标准答案</div>
              <div>{{ q.correctAnswer || '—' }}</div>
            </div>
            <div class="answer-review-analysis">
              <div class="small text-muted mb-1">解析</div>
              <div>{{ q.analysis || '暂无解析' }}</div>
              <div v-if="analysisImageList(q).length" class="mt-2 d-flex flex-wrap gap-2">
                <img
                  v-for="(u, ai) in analysisImageList(q)"
                  :key="'rev-ana-' + q.questionId + '-' + ai"
                  :src="u"
                  class="exam-q-img-thumb"
                  alt=""
                  loading="lazy"
                  @click="openImageLightbox(u)"
                />
              </div>
            </div>
          </div>
          <div v-if="!answerReview.questions.length" class="text-muted">暂无题目数据</div>
        </div>
      </section>

      <section
        class="card-box exam-wait-shell exam-entry-strong"
        v-if="currentTab === 'exam' && examWaitContext && !activeExam && !precheckContext"
      >
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">等待开考：{{ examWaitContext.exam.examName }}</h4>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelExamWait">返回考试列表</button>
        </div>
        <p class="text-muted small mb-3">
          您已完成考前四步核验，核验记录已保存。教师「发布」且到达开考时间后，可点击下方「进入考试」；到达考试结束时间后将无法进入。
        </p>
        <div class="exam-wait-countdown mb-3">
          <div class="small text-muted mb-1">距离开考还有</div>
          <div class="exam-wait-countdown-digits">{{ examWaitCountdownText }}</div>
          <div class="small mt-2">开考时间：{{ $fmtDateTime(examWaitContext.exam.startTime) }}</div>
          <div class="small">结束时间：{{ $fmtDateTime(examWaitContext.exam.endTime) }}</div>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!examWaitCanEnterNow"
            @click="tryEnterFromWait"
          >
            进入考试
          </button>
          <span v-if="!examWaitPublishedOk" class="small text-warning align-self-center">
            教师尚未发布本场考试，无法开考。请联系监考老师在「考试管理」中点击「发布」后再刷新本页。
          </span>
          <span
            v-else-if="examWaitPastEndNow"
            class="small text-muted align-self-center"
          >考试已结束，无法进入。</span>
          <span
            v-else-if="!examWaitCanEnterNow && examWaitPublishedOk"
            class="small text-muted align-self-center"
          >距离开考请等候上方倒计时结束后再点击「进入考试」。</span>
        </div>
      </section>

      <section class="card-box exam-entry-strong" v-if="currentTab === 'exam' && !activeExam && !precheckContext && !examWaitContext">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">在线考试</h4>
          <button class="btn btn-light btn-sm" @click="loadExamData">刷新</button>
        </div>
         <p class="exam-list-hint small mb-2 mb-md-3">
          <template v-if="isAutomanLoggedInUser">
            Automan 账号可直接进入考试（免考前设备核验）；未到开考时间将进入等候区。
          </template>
          <template v-else>
            可先完成考前核验；在开考时间之后方可进入答卷界面（未到开考将进入等候区）。
          </template>
        </p>
        <div class="result-shell">
          <table class="table table-striped align-middle modern-table">
            <thead>
              <tr><th>考试名称</th><th>时间</th><th>状态</th><th>操作</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in availableExams" :key="'exam-' + item.exam.id">
                <td>{{ item.exam.examName }}</td>
                <td>{{ $fmtDateTime(item.exam.startTime) }} ~ {{ $fmtDateTime(item.exam.endTime) }}</td>
                <td>{{ displayExamStatusLabel(item.exam) }}</td>
                <td><button class="btn btn-sm btn-primary" @click="openPrecheck(item)">进入考试</button></td>
              </tr>
              <tr v-if="availableExams.length === 0"><td colspan="4" class="text-muted">暂无可参加考试</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="card-box precheck-shell exam-entry-strong" v-if="currentTab === 'exam' && precheckContext && !activeExam">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">考前核验：{{ precheckContext.exam.examName }}</h4>
          <button class="btn btn-light btn-sm" @click="cancelPrecheck">取消</button>
        </div>

        <div class="precheck-step mb-3">
          <span v-if="networkOk" class="step-success-badge">成功</span>
          <div class="step-title">第一步：网络检测</div>
          <p class="small text-muted mb-2">点击“测试当前网络”，验证当前网络可用后才能进行下一步。</p>
          <div class="d-flex gap-2 align-items-center">
            <button class="btn btn-light btn-sm" @click="testNetwork">测试当前网络</button>
            <span :class="networkOk ? 'text-success' : 'text-warning'">{{ networkStatusText }}</span>
          </div>
        </div>

        <div class="precheck-step mb-3" :class="{ disabled: !networkOk }">
          <span v-if="photoDataUrl" class="step-success-badge">成功</span>
          <div class="step-title">第二步：摄像头拍摄</div>
          <p class="small text-muted mb-2">
            调用本机摄像头进行拍照，照片将与考试记录关联，供管理员查看，<span class="precheck-emphasis">若无法启用摄像头，可以自行上传一张人像通过检测。</span>
          </p>
          <div class="row g-2">
            <div class="col-md-6">
              <video ref="cameraVideo" class="camera-box" autoplay playsinline muted></video>
              <div class="d-flex gap-2 mt-2">
                <button class="btn btn-light btn-sm" :disabled="!networkOk" @click="startCamera">开启摄像头</button>
                <button class="btn btn-light btn-sm" :disabled="!cameraReady" @click="capturePhoto">拍照</button>
                <button class="btn btn-light btn-sm" :disabled="!photoDataUrl" @click="retakePhoto">重拍</button>
                <button class="btn btn-light btn-sm" @click="$refs.photoUploadInput && $refs.photoUploadInput.click()">上传照片</button>
                <input ref="photoUploadInput" type="file" accept="image/*" class="d-none" @change="onPhotoUpload" />
              </div>
              <div v-if="cameraError" class="small text-warning mt-2">{{ cameraError }}</div>
            </div>
            <div class="col-md-6">
              <div class="small mb-1">拍摄结果：</div>
              <img v-if="photoDataUrl" :src="photoDataUrl" class="photo-preview" alt="student-photo" />
              <div v-else class="photo-placeholder">未拍照</div>
            </div>
          </div>
        </div>

        <div class="precheck-step mb-3" :class="{ disabled: !photoDataUrl }">
          <span v-if="precheckMicStepDone" class="step-success-badge">{{ precheckMicBadgeText }}</span>
          <div class="step-title">第三步：麦克风检测</div>
          <p class="small text-muted mb-2">
            考试期间监考教师可能通过<strong>语音</strong>与您沟通。请点击下方按钮检测本机麦克风是否可被浏览器调用。
          </p>
          <div class="d-flex flex-wrap gap-2 align-items-center mb-2">
            <button
              type="button"
              class="btn btn-light btn-sm"
              :disabled="!photoDataUrl || precheckMicStatus === 'testing'"
              @click="testPrecheckMicrophone"
            >
              {{ precheckMicStatus === 'testing' ? '检测中…' : '检测麦克风' }}
            </button>
            <span v-if="precheckMicStatus === 'ok'" class="text-success small fw-semibold">麦克风已接通，本步已通过。</span>
            <span v-else-if="precheckMicStatus === 'testing'" class="small text-muted">正在请求麦克风权限…</span>
          </div>
          <div v-if="precheckMicStatus === 'fail'" class="alert alert-warning py-2 mb-0 small">
            <div class="fw-semibold mb-1">未能接通麦克风（被拒绝、无设备或不可用）</div>
            <p class="mb-2">
              <strong>后果说明：</strong>若麦克风不可用，考试过程中您<strong>将无法与监考教师进行语音沟通</strong>（例如听不到教师发起的语音说明或实时沟通），可能错过重要提醒，影响监考联络与应急处理。<strong>由此带来的不便与风险由您自行承担。</strong>
            </p>
            <p class="mb-2 text-muted">您仍可继续参加笔试作答；若坚持使用无声环境，请点击下方确认已知晓上述后果。</p>
            <button type="button" class="btn btn-sm btn-outline-dark" :disabled="micFailAcknowledged" @click="ackPrecheckMicFail">
              {{ micFailAcknowledged ? '已确认，可继续后续步骤' : '我已阅读并了解上述后果，仍继续后续步骤' }}
            </button>
          </div>
        </div>

        <div class="precheck-step mb-3" :class="{ disabled: !precheckMicStepDone }">
          <span v-if="stepFourOk" class="step-success-badge">成功</span>
          <div class="step-title">第四步：键鼠可用性验证</div>
          <p class="small text-muted mb-2">请用键盘输入指定内容，并点击按钮验证鼠标。</p>
          <div class="row g-2 align-items-end">
            <div class="col-md-5">
              <label class="small mb-1"><span class="precheck-emphasis">请通过键盘输入：考试系统正常</span></label>
              <input v-model="deviceCheckAnswer" class="form-control" placeholder="请输入指定文字" :disabled="!precheckMicStepDone" />
            </div>
            <div class="col-md-3">
              <button type="button" class="btn btn-light w-100" :disabled="!precheckMicStepDone" @click="confirmMouseClick">点击验证鼠标</button>
            </div>
            <div class="col-md-4 small">
              <div :class="keyboardOk ? 'status-ok' : 'status-fail'">键盘检测：{{ keyboardOk ? '通过' : '未通过' }}</div>
              <div :class="mouseOk ? 'status-ok' : 'status-fail'">鼠标检测：{{ mouseOk ? '通过' : '未通过' }}</div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-primary ms-auto" :disabled="!canEnterExam" @click="finishPrecheckAndEnter">进入正式考试</button>
        </div>
        <div v-if="precheckSaveOkMessage" class="alert alert-success py-2 mt-2 mb-0">
          {{ precheckSaveOkMessage }}
        </div>
      </section>

      <div
        v-if="currentTab === 'exam' && activeExam"
        ref="examSecureHost"
        class="exam-secure-root exam-entry-strong"
        tabindex="-1"
        @contextmenu.prevent
        @copy.prevent
        @cut.prevent
        @paste.prevent
      >
        <section class="card-box exam-secure-card">
          <div class="exam-exam-top-bar mb-2">
            <h4 class="mb-0 exam-exam-top-title">正在考试：{{ activeExam.exam.examName }}</h4>
            <div class="exam-countdown-line">
              比赛进行中，倒计时为：<span class="exam-countdown-time">{{ countdownText }}</span>
            </div>
          </div>

          <div v-if="showResumeTip" class="alert alert-info py-2 mb-2 d-flex justify-content-between align-items-center">
            <span>已恢复上次作答进度（题号位置与已保存答案）。</span>
            <button class="btn btn-sm btn-light" @click="showResumeTip = false">知道了</button>
          </div>

          <div class="exam-layout" :class="{ 'exam-layout--automan': isAutomanLoggedInUser }">
            <aside class="exam-left-rail" :class="{ 'exam-left-rail--proctor-only': isAutomanLoggedInUser }">
              <div v-if="!isAutomanLoggedInUser" class="camera-monitor mb-2">
                <div class="monitor-title exam-side-block-title">摄像头实时监控</div>
                <video ref="examMonitorVideo" class="monitor-video" autoplay playsinline muted></video>
                <div
                  class="exam-monitor-status-line"
                  :class="examCameraReady ? 'monitor-ok' : 'monitor-fail'"
                >
                  {{ examCameraStatusText }}
                </div>
                <button type="button" class="btn btn-light mt-2 w-100 exam-side-btn" @click="startExamCameraMonitor(false)">
                  重试开启监控
                </button>
              </div>

              <div class="exam-hand-panel" :class="{ 'mb-2': !isAutomanLoggedInUser }">
                <div class="monitor-title exam-side-block-title mb-1">监考联络</div>
                <template v-if="examHandUiMode === 'idle'">
                  <p class="exam-hand-body exam-hand-body--muted mb-2">
                    需要联系监考教师时可举手；教师响应后将开通语音与文字通道。
                  </p>
                  <button type="button" class="btn btn-warning w-100 exam-side-btn" @click="studentRaiseHand">举手</button>
                </template>
                <template v-else-if="examHandUiMode === 'pending'">
                  <p class="exam-hand-body exam-hand-body--info mb-2 mb-md-1">已举手，请等待监考教师响应…</p>
                  <button type="button" class="btn btn-outline-secondary w-100 exam-side-btn" @click="studentCancelRaiseHand">
                    取消举手
                  </button>
                </template>
                <template v-else-if="examHandUiMode === 'responded'">
                  <p class="exam-hand-body exam-hand-body--success mb-2 mb-md-1">
                    监考教师已响应。请保持本页，注意收听（若已授权麦克风）；也可在下方与教师文字沟通。
                  </p>
                  <div ref="examDmScroll" class="exam-dm-log exam-dm-log--sized mb-2">
                    <div v-for="m in examDmMessages" :key="'dm-' + m.id" class="exam-dm-line">
                      <span :class="m.fromRole === 'teacher' ? 'text-warning' : 'text-light'">
                        {{ m.fromRole === 'teacher' ? '教师' : '我' }}：
                      </span>
                      {{ m.content }}
                    </div>
                    <div v-if="examDmMessages.length === 0" class="text-muted">暂无消息</div>
                  </div>
                  <div class="d-flex gap-1">
                    <input
                      v-model="examDmInput"
                      class="form-control exam-dm-input"
                      placeholder="输入文字发送给教师…"
                      maxlength="2000"
                      @keydown.enter.prevent="sendStudentDm"
                    />
                    <button type="button" class="btn btn-primary flex-shrink-0 exam-side-btn exam-side-btn--compact" :disabled="examDmSending" @click="sendStudentDm">
                      发送
                    </button>
                  </div>
                </template>
              </div>
            </aside>

            <div class="exam-center-col">
              <div class="question-panel" v-if="currentQuestion">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <strong>第 {{ currentQuestionIndex + 1 }} 题</strong>
                  <span class="ms-2 text-muted">{{ examQuestionTypeLabel(currentQuestion.type) }} / {{ currentQuestion.score }}分</span>
                </div>
                <button class="btn btn-sm btn-light" @click="toggleMark(currentQuestion.id)">
                  {{ markedQuestions[currentQuestion.id] ? '取消标记' : '标记题目' }}
                </button>
              </div>

              <div class="question-title question-stem-block mb-2">
                <div class="question-stem-label">题干：</div>
                <div class="question-stem-content">{{ currentQuestion.title }}</div>
              </div>
              <div v-if="stemImageList(currentQuestion).length" class="question-stem-images mb-1">
                <img
                  v-for="(u, si) in stemImageList(currentQuestion)"
                  :key="'stem-img-' + currentQuestion.id + '-' + si"
                  :src="u"
                  class="exam-q-img-thumb me-2 mb-2"
                  alt="题干插图"
                  loading="lazy"
                  tabindex="0"
                  title="单击放大，滚轮缩放"
                  @click.stop="openImageLightbox(u)"
                />
              </div>
              <div v-if="currentQuestion.type === 'coding'" class="coding-exam-block">
                <div v-if="codingIoSpec(currentQuestion)" class="small text-muted mb-2 coding-io-spec">
                  <strong>题目说明：</strong>
                  <div class="coding-io-spec-body">{{ codingIoSpec(currentQuestion) }}</div>
                </div>
                <div class="coding-exam-row">
                  <div class="coding-exam-editor">
                    <div class="coding-exam-panel-head">
                      <label class="small mb-0">代码作答区</label>
                      <div class="d-flex align-items-center gap-1 flex-wrap">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary text-nowrap"
                          :disabled="codingRunLoadingId === currentQuestion.id || !activeExam || !activeExam.record"
                          @click="saveStudentPythonCodeOnly"
                        >
                          保存代码
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-primary text-nowrap"
                          :disabled="codingRunLoadingId === currentQuestion.id || !activeExam || !activeExam.record"
                          @click="runStudentPythonCode"
                        >
                          {{ codingRunLoadingId === currentQuestion.id ? '运行中…' : '运行代码' }}
                        </button>
                      </div>
                    </div>
                    <CodingLineTextarea
                      :key="'coding-' + currentQuestion.id"
                      v-model="answers[currentQuestion.id]"
                      @input="markDirty(currentQuestion.id)"
                      @keydown="onCodingTextareaKeydown($event, currentQuestion.id)"
                    />
                  </div>
                  <div class="coding-exam-output">
                    <div class="coding-exam-panel-head coding-exam-panel-head--output">
                      <span class="small mb-0">运行输出</span>
                    </div>
                    <pre class="coding-exam-output-pre" role="status">{{ codingOutputText(currentQuestion.id) }}</pre>
                  </div>
                </div>
              </div>

              <div v-else-if="String(currentQuestion.type || '').toLowerCase() === 'material_collect'" class="material-collect-exam">
                <p class="small text-muted mb-2">
                  请填写文字说明，并上传 <strong>{{ materialMaxCount(currentQuestion) }}</strong> 个附件（缺一不可）。单个文件不超过 10MB；格式：jpg/png/jpeg/gif、pdf、word(doc/docx)、zip、rar。
                </p>
                <label class="form-label small mb-1">文字说明</label>
                <textarea
                  class="form-control mb-3"
                  rows="5"
                  placeholder="请填写说明…"
                  :value="materialText(currentQuestion.id)"
                  @input="setMaterialText(currentQuestion.id, $event.target.value)"
                ></textarea>
                <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <label class="btn btn-sm btn-primary mb-0">
                    选择附件上传
                    <input type="file" class="d-none" @change="onMaterialFileChange($event, currentQuestion)" />
                  </label>
                  <span class="small text-muted">已上传 {{ materialAttachments(currentQuestion.id).length }} / {{ materialMaxCount(currentQuestion) }}</span>
                </div>
                <ul v-if="materialAttachments(currentQuestion.id).length" class="list-group list-group-flush border rounded mb-0">
                  <li
                    v-for="(att, mi) in materialAttachments(currentQuestion.id)"
                    :key="'matt-' + currentQuestion.id + '-' + mi"
                    class="list-group-item d-flex justify-content-between align-items-center py-2"
                  >
                    <div class="small text-truncate me-2" style="max-width: 60%">
                      {{ att.originalName || att.storageName || '附件' }}（{{ materialFileSizeLabel(att.size) }}）
                    </div>
                    <div class="d-flex gap-2 flex-shrink-0">
                      <a v-if="att.viewUrl" class="btn btn-sm btn-outline-secondary" :href="att.viewUrl" target="_blank" rel="noopener">预览</a>
                      <a v-if="att.downloadUrl" class="btn btn-sm btn-outline-secondary" :href="att.downloadUrl" target="_blank" rel="noopener">下载</a>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="removeMaterialAttachment(currentQuestion.id, mi)">移除</button>
                    </div>
                  </li>
                </ul>
              </div>

              <div v-else-if="String(currentQuestion.type || '').toLowerCase() === 'scratch'" class="scratch-exam-block">
                <div v-if="currentQuestion.analysis" class="small mb-2">
                  <strong>任务要求：</strong>
                  <div class="question-stem-content mt-1">{{ currentQuestion.analysis }}</div>
                </div>
                <button
                  v-if="activeExam && activeExam.record"
                  type="button"
                  class="btn btn-primary"
                  @click="goStudentScratchExamPage"
                >前往 Scratch 答题页</button>
                <div v-if="scratchUploadLabel(currentQuestion.id)" class="small text-success mt-2">
                  {{ scratchUploadLabel(currentQuestion.id) }}
                </div>
              </div>

              <div v-else-if="currentQuestion.type === 'multiple'" class="exam-single-wrap">
                <div class="small mb-2 text-muted">请选择一个或多个答案（点击选项即可，可多选）</div>
                <div
                  class="exam-choice-list"
                  role="listbox"
                  aria-multiselectable="true"
                  :aria-label="'第' + (currentQuestionIndex + 1) + '题选项'"
                >
                  <button
                    v-for="opt in optionEntries(currentQuestion)"
                    :key="'m-' + opt.key"
                    type="button"
                    class="exam-choice-btn exam-choice-btn--rich"
                    :class="{ active: isMultiChecked(currentQuestion.id, opt.key) }"
                    :aria-selected="isMultiChecked(currentQuestion.id, opt.key)"
                    @click="toggleMulti(currentQuestion.id, opt.key)"
                  >
                    <span class="exam-choice-main">
                      <span class="exam-choice-key">{{ opt.key }}.</span>
                      <span class="exam-choice-text">{{ opt.value }}</span>
                    </span>
                    <span v-if="opt.imageUrl" class="exam-choice-img" @click.stop>
                      <img
                        :src="opt.imageUrl"
                        class="exam-q-img-thumb"
                        alt=""
                        loading="lazy"
                        title="单击放大（打开大图）"
                        tabindex="0"
                        @click.stop.prevent="openImageLightbox(opt.imageUrl)"
                      />
                    </span>
                  </button>
                </div>
              </div>

              <div v-else class="exam-single-wrap">
                <div class="small mb-2 text-muted">请选择一个答案（点击选项即可）</div>
                <div class="exam-choice-list" role="listbox" :aria-label="'第' + (currentQuestionIndex + 1) + '题选项'">
                  <button
                    v-for="opt in optionEntries(currentQuestion)"
                    :key="'r-' + opt.key"
                    type="button"
                    class="exam-choice-btn exam-choice-btn--rich"
                    :class="{ active: String(getAnswerForQuestion(currentQuestion.id) || '') === String(opt.key) }"
                    @click="selectSingleChoice(currentQuestion.id, opt.key)"
                  >
                    <span class="exam-choice-main">
                      <span class="exam-choice-key">{{ opt.key }}.</span>
                      <span class="exam-choice-text">{{ opt.value }}</span>
                    </span>
                    <span v-if="opt.imageUrl" class="exam-choice-img" @click.stop>
                      <img
                        :src="opt.imageUrl"
                        class="exam-q-img-thumb"
                        alt=""
                        loading="lazy"
                        title="单击放大（打开大图）"
                        tabindex="0"
                        @click.stop.prevent="openImageLightbox(opt.imageUrl)"
                      />
                    </span>
                  </button>
                </div>
              </div>

              <div
                v-if="shouldShowExamAnalysis && currentQuestion && (currentQuestion.analysis || analysisImageList(currentQuestion).length)"
                class="exam-analysis-box mt-3 p-3 rounded"
              >
                <div class="small text-muted mb-1">解析</div>
                <div class="exam-analysis-text">{{ currentQuestion.analysis }}</div>
                <div v-if="analysisImageList(currentQuestion).length" class="mt-2 d-flex flex-wrap gap-2">
                  <img
                    v-for="(u, ai) in analysisImageList(currentQuestion)"
                    :key="'ana-' + currentQuestion.id + '-' + ai"
                    :src="u"
                    class="exam-q-img-thumb"
                    alt="解析插图"
                    loading="lazy"
                    title="单击放大"
                    tabindex="0"
                    @click.stop="openImageLightbox(u)"
                  />
                </div>
              </div>

              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-light" :disabled="currentQuestionIndex === 0" @click="currentQuestionIndex -= 1">上一题</button>
                <button class="btn btn-light" :disabled="currentQuestionIndex >= examQuestions.length - 1" @click="currentQuestionIndex += 1">下一题</button>
                <button class="btn btn-primary ms-auto" :disabled="examSubmitInProgress" @click="submitExamConfirm">交卷</button>
              </div>
              </div>
              <div v-else class="question-panel exam-center-empty d-flex align-items-center justify-content-center text-muted">
                试题加载中或暂无题目…
              </div>
            </div>

            <aside class="exam-right-rail">
              <div class="exam-nav-card">
                <div class="exam-nav-card-body">
                  <div class="exam-nav-card-heading">题目导航（可滚轮滚动）</div>
                  <div class="q-grid">
                    <button
                      v-for="(q, idx) in examQuestions"
                      :key="'nav-' + q.id"
                      class="q-no-btn"
                      :class="{
                        active: currentQuestionIndex === idx,
                        marked: !!markedQuestions[q.id],
                        answered: isAnswered(q.id),
                        restored: restoredQuestionIndex === idx
                      }"
                      ref="qNavBtns"
                      @click="currentQuestionIndex = idx"
                    >
                      {{ idx + 1 }}
                    </button>
                  </div>
                  <div class="nav-summary mt-3">
                    <div>已完成：<span class="num-done">{{ answeredCount }}</span> 题</div>
                    <div>未完成：<span class="num-pending">{{ pendingCount }}</span> 题</div>
                  </div>
                </div>
                <div
                  class="exam-nav-card-foot exam-anti-inline-red"
                  title="请全程在本页作答；勿切换窗口或使用复制、粘贴、右键。"
                >
                  <div class="exam-anti-line exam-anti-line--lead">防作弊监测：已禁用复制、粘贴、剪切与鼠标右键；请保持在本浏览器页面作答。</div>
                  <div class="exam-anti-line exam-anti-line--detail">
                    切换窗口或标签页将记为切屏；当前累计 <strong>{{ liveScreenCount }}</strong> / {{ screenCheatLimit }} 次，满 {{ screenCheatLimit }} 次将强制交卷。
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </div>

      <div
        v-if="showSubmitConfirmModal"
        class="modal-mask exam-anti-modal exam-submit-confirm-mask"
        @click.self="closeSubmitConfirmModal"
      >
        <div class="modal-panel exam-submit-confirm-panel" @click.stop>
          <h5 class="mb-3">确认交卷</h5>
          <p class="mb-2">
            您当前答卷进度：<strong>已完成 {{ answeredCount }} 题</strong>，<strong>未完成 {{ pendingCount }} 题</strong>（共
            {{ examQuestions.length }} 题）。
          </p>
          <p class="mb-3 fw-bold exam-submit-final-warn">
            交卷成功后，所有答案将正式提交且无法再修改或继续作答，请确认无误后再操作。
          </p>
          <p v-if="submitConfirmSecondsLeft > 0" class="exam-submit-countdown-hint mb-3">
            请认真阅读以上说明，<strong>{{ submitConfirmSecondsLeft }}</strong> 秒后可点击「确定交卷」。
          </p>
          <div class="d-flex flex-wrap gap-2 justify-content-end">
            <button type="button" class="btn btn-outline-light" @click="closeSubmitConfirmModal">取消，返回试卷</button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="submitConfirmSecondsLeft > 0 || examSubmitInProgress"
              @click="confirmSubmitExamFinal"
            >
              {{
                submitConfirmSecondsLeft > 0
                  ? `确定交卷（${submitConfirmSecondsLeft} 秒）`
                  : '确定交卷'
              }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="showExamBroadcastModal"
        class="modal-mask exam-broadcast-modal"
        @click.self="closeExamBroadcastModal()"
      >
        <div class="modal-panel exam-broadcast-panel" @click.stop>
          <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
            <h5 class="mb-0 text-primary">考场通知</h5>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeExamBroadcastModal()">
              关闭
            </button>
          </div>
          <div class="exam-broadcast-body">{{ examBroadcastContent }}</div>
          <p class="small text-muted mt-3 mb-0">
            本窗口将在约 {{ examBroadcastRemainSeconds }} 秒后自动关闭（共展示约 1 分钟），您也可随时点「关闭」，不会记为切屏作弊。
          </p>
        </div>
      </div>

      <audio ref="examVoiceRemoteAudio" class="d-none" autoplay playsinline></audio>

      <div v-if="showScreenAckModal" class="modal-mask exam-anti-modal">
        <div class="modal-panel">
          <h5 class="text-warning">切屏提示</h5>
          <p class="mb-2">
            检测到您已切换窗口或离开本页（第 {{ screenCheatAckCount }} 次 / 最多 {{ screenCheatLimit }} 次）。为继续作答，请在下方完整输入「我已知晓」。
          </p>
          <label class="small mb-1">请输入：<strong>我已知晓</strong></label>
          <input v-model="screenAckInput" class="form-control mb-3" placeholder="我已知晓" autocomplete="off" />
          <div class="d-flex gap-2">
            <button class="btn btn-success flex-fill" @click="confirmScreenAck">确定并继续作答</button>
          </div>
        </div>
      </div>

      <div v-if="showScreenForceModal" class="modal-mask exam-anti-modal">
        <div class="modal-panel">
          <h5 class="text-danger">强制交卷</h5>
          <p class="mb-3">
            您累计切屏已达 <strong>{{ screenCheatLimit }} 次</strong>，系统将强制交卷。点击「确定交卷」后，本次作答将立即提交，无法继续修改。
          </p>
          <button class="btn btn-danger w-100" @click="confirmForceSubmitByScreen">确定交卷</button>
        </div>
      </div>

      <div v-if="showSecondScreenModal" class="modal-mask exam-anti-modal">
        <div class="modal-panel">
          <h5 class="text-danger">检测到副屏/多屏</h5>
          <p class="mb-2">
            系统检测到当前设备连接了 <strong>{{ secondScreenCount }}</strong> 个显示器（含副屏）。
            为保证考试公平，请拔掉副屏接口或关闭“扩展显示”，仅保留一个屏幕后再进入/继续考试。
          </p>
          <p class="small text-muted mb-3">
            提示：拔掉副屏后请点击「重新检测」。若浏览器未授权屏幕枚举权限，也会导致无法通过检测。
          </p>
          <div class="d-flex gap-2">
            <button class="btn btn-primary flex-fill" :disabled="secondScreenChecking" @click="recheckSecondScreen">
              {{ secondScreenChecking ? '检测中...' : '重新检测' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="showNoticeModal" class="modal-mask notice-modal-mask" @click.self="closeNoticeModal">
        <div class="modal-panel notice-modal-panel">
          <div class="notice-modal-header">
            <div>
              <h4 class="mb-1">{{ currentNotice.title || '公告详情' }}</h4>
              <div class="small text-muted">
                发布人：{{ currentNotice.publisher || '管理员' }} · 发布时间：{{ $fmtDateTime(currentNotice.publishTime || currentNotice.createTime) }}
              </div>
            </div>
            <button class="btn btn-sm btn-light" @click="closeNoticeModal">关闭</button>
          </div>

          <div class="notice-layout">
            <div class="notice-modal-section notice-main-section">
              <div class="notice-section-title">公告正文</div>
              <div class="notice-modal-text">{{ currentNoticeParsed.text || '（无文字内容）' }}</div>
            </div>

            <div class="notice-modal-section notice-side-section">
              <div class="notice-section-title">附件内容（{{ currentNoticeParsed.assets.length }}）</div>
              <div v-if="currentNoticeParsed.assets.length > 0" class="notice-asset-list">
                <div v-for="(asset, idx) in currentNoticeParsed.assets" :key="'asset-view-' + idx" class="notice-asset-item">
                  <div class="notice-asset-name">{{ asset.originalName || '附件' }}</div>
                  <div class="small text-muted mb-2">{{ asset.contentType || 'application/octet-stream' }}</div>
                  <div v-if="isImageAsset(asset)" class="mb-2">
                    <img :src="normalizeAssetUrl(asset.viewUrl || asset.downloadUrl)" class="notice-asset-image" alt="notice-image" />
                  </div>
                  <div class="notice-asset-actions">
                    <button class="btn btn-sm btn-light" @click="openAsset(asset)">在线打开</button>
                    <button class="btn btn-sm btn-primary" @click="downloadAsset(asset)">下载文件</button>
                  </div>
                </div>
              </div>
              <div v-else class="small text-muted">暂无附件</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="imageLightboxUrl" class="modal-mask exam-img-lightbox-mask" @click.self="closeImageLightbox">
        <div class="exam-img-lightbox-panel" @click.stop @wheel.prevent="onImageLightboxWheel">
          <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
            <span class="small exam-img-lightbox-hint">点击背景关闭 · 滚轮或按钮缩放</span>
            <div class="d-flex flex-wrap gap-1 align-items-center">
              <button type="button" class="btn btn-sm btn-outline-light" @click="imageLightboxScale = Math.min(4, imageLightboxScale + 0.2)">放大</button>
              <button type="button" class="btn btn-sm btn-outline-light" @click="imageLightboxScale = Math.max(0.25, imageLightboxScale - 0.2)">缩小</button>
              <button type="button" class="btn btn-sm btn-outline-light" @click="imageLightboxScale = 1">复位</button>
              <button type="button" class="btn btn-sm btn-light" @click="closeImageLightbox">关闭</button>
            </div>
          </div>
          <div class="exam-img-zoom-viewport">
            <img
              :src="imageLightboxUrl"
              class="exam-q-img-lightbox"
              alt="大图预览"
              :style="{ transform: 'scale(' + imageLightboxScale + ')', transformOrigin: 'center center' }"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { parseUrlList, resolveOptionImage, optionEntriesFromRow } from '../utils/questionRichMedia'
import {
  MATERIAL_COLLECT_TYPE,
  isMaterialAnswerComplete,
  materialSubmitBlockedMessage,
  parseMaterialAnswer,
  parseMaxAttachments,
  stringifyMaterialAnswer,
  validateMaterialFile
} from '../utils/materialCollect'
import {
  SCRATCH_TYPE,
  isScratchAnswerComplete,
  parseScratchAnswer,
  scratchSubmitBlockedMessage,
  stringifyScratchAnswer
} from '../utils/scratchExam'
import { preloadPyodide, runPythonInBrowser } from '../utils/pyodideRunner'
import CodingLineTextarea from '../components/CodingLineTextarea.vue'
export default {
  name: 'StudentHomeView',
  components: { CodingLineTextarea },
  data() {
    return {
      /** 从 Scratch 独立页返回时恢复题号 */
      pendingScratchQuestionIndex: null,
      user: {},
      currentTab: 'home',
      scoreRows: [],
      answerReview: {
        loading: false,
        showAnswerAfterSubmit: 0,
        allowRedo: 0,
        examName: '',
        recordId: null,
        message: '',
        questions: [],
        returnTab: 'home'
      },
      examRecords: [],
      notices: [],
      exams: [],
      papers: [],
      paperQuestions: [],
      questionMap: {},
      activeExam: null,
      examQuestions: [],
      /** 本场考试是否允许学生端展示解析（来自 exam_config.show_analysis） */
      examShowAnalysis: true,
      currentQuestionIndex: 0,
      answers: {},
      markedQuestions: {},
      dirtyQuestionIds: {},
      countdownSeconds: 0,
      countdownTimer: null,
      autosaveTimer: null,
      showResumeTip: false,
      imageLightboxUrl: null,
      imageLightboxScale: 1,
      restoredQuestionIndex: null,
      restoreTipTimer: null,

      precheckContext: null,
      networkOk: false,
      networkStatusText: '未检测',
      networkLatencyMs: null,
      cameraReady: false,
      cameraStream: null,
      photoDataUrl: '',
      cameraError: '',
      deviceCheckAnswer: '',
      mouseOk: false,
      precheckMicStream: null,
      precheckMicStatus: '',
      micFailAcknowledged: false,
      precheckSaveOkMessage: '',

      examWaitContext: null,
      examWaitTimer: null,
      examWaitTickMs: Date.now(),

      examCameraReady: false,
      examCameraStatusText: '监控未开启',
      examCameraStream: null,
      examCameraMonitorTimer: null,
      examMonitorUploadTimer: null,
      lastCameraAnomalyType: '',
      lastCameraAnomalyAt: 0,
      cameraHeavyReportAt: {},

      lastScreenViolMs: 0,
      ignoreScreenUntil: 0,
      lastBehaviorReportMs: 0,
      screenCheatNeedAck: false,
      screenCheatPendingForce: false,
      screenForceSubmitting: false,
      screenCheatAckCount: 0,
      showScreenAckModal: false,
      showScreenForceModal: false,
      showSecondScreenModal: false,
      secondScreenCount: 1,
      secondScreenChecking: false,
      pendingEnterExamItem: null,
      secondScreenDetails: null,
      boundSecondScreenChange: null,
      secondScreenPollTimer: null,
      secondScreenGuardToken: 0,
      enableSecondScreenCheck: false,
      showSubmitConfirmModal: false,
      submitConfirmSecondsLeft: 0,
      submitConfirmTimer: null,
      examSubmitInProgress: false,
      screenAckInput: '',
      boundAntiVis: null,
      boundAntiKey: null,
      boundAntiCtx: null,
      boundAntiPaste: null,
      showNoticeModal: false,
      currentNotice: {},
      currentNoticeParsed: { text: '', assets: [] },
      noticeSeenRevisions: {},
      noticeListPollTimer: null,

      examBroadcastPollTimer: null,
      broadcastPollAfterId: 0,
      examBroadcastQueue: [],
      showExamBroadcastModal: false,
      examBroadcastContent: '',
      examBroadcastRemainSeconds: 60,
      examBroadcastTickTimer: null,
      examBroadcastCurrentId: null,
      examAnticheatMutedUntil: 0,

      examVoicePc: null,
      examVoiceLocalStream: null,
      examVoicePullTimer: null,
      examVoicePollTimer: null,
      examVoiceLastPullId: 0,
      examVoiceTeacherIceQueue: [],

      examHandRemoteStatus: 'NONE',
      examHandPollTimer: null,
      examDmPollTimer: null,
      examDmLastId: 0,
      examDmMessages: [],
      examDmInput: '',
      examDmSending: false,

      codingRunByQuestion: {},
      codingRunLoadingId: null,
      /** 本场考试是否已触发过 Pyodide 预加载（多道 Python 题也只预加载一次） */
      pyodideRuntimeWarmStarted: false
    }
  },
  computed: {
    availableExams() {
      return this.examRecords
        .map((r) => ({ record: r, exam: this.exams.find((e) => String(e.id) === String(r.examId)) }))
        .filter((x) => x.exam)
        .filter((x) => this.examNotPastEnd(x.exam))
    },
    myExamInfos() {
      return this.examRecords
        .map((r) => {
          const exam = this.exams.find((e) => String(e.id) === String(r.examId))
          if (!exam) return null
          return {
            recordId: r.id,
            record: r,
            exam,
            examName: exam.examName,
            startTime: exam.startTime,
            endTime: exam.endTime,
            status: exam.status
          }
        })
        .filter(Boolean)
        .filter((a) => this.examNotPastEnd(a.exam))
        .sort((a, b) => {
          const bt = new Date(String(b.startTime || '').replace(' ', 'T')).getTime() || 0
          const at = new Date(String(a.startTime || '').replace(' ', 'T')).getTime() || 0
          return bt - at
        })
    },
    currentQuestion() {
      return this.examQuestions[this.currentQuestionIndex] || null
    },
    countdownText() {
      const s = Math.max(0, Number(this.countdownSeconds || 0))
      const h = String(Math.floor(s / 3600)).padStart(2, '0')
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
      const sec = String(s % 60).padStart(2, '0')
      return `${h}:${m}:${sec}`
    },
    keyboardOk() {
      return String(this.deviceCheckAnswer || '').trim() === '考试系统正常'
    },
    precheckMicStepDone() {
      if (this.precheckMicStatus === 'ok') return true
      return this.precheckMicStatus === 'fail' && this.micFailAcknowledged
    },
    precheckMicBadgeText() {
      if (this.precheckMicStatus === 'ok') return '通过'
      if (this.precheckMicStatus === 'fail' && this.micFailAcknowledged) return '已确认'
      return ''
    },
    canEnterExam() {
      return this.networkOk && !!this.photoDataUrl && this.precheckMicStepDone && this.keyboardOk && this.mouseOk
    },
    isAutomanLoggedInUser() {
      const id = this.user && this.user.automanUserId
      const n = Number(id)
      return Number.isFinite(n) && n > 0
    },
    stepFourOk() {
      return this.keyboardOk && this.mouseOk
    },
    answeredCount() {
      return this.examQuestions.filter((q) => this.isAnswered(q.id)).length
    },
    pendingCount() {
      return Math.max(0, this.examQuestions.length - this.answeredCount)
    },
    liveScreenCount() {
      const r = this.activeExam && this.activeExam.record
      if (!r) return 0
      return Number(r.screenCount || 0)
    },
    screenCheatLimit() {
      const r = this.activeExam && this.activeExam.record
      if (!r) return 5
      const lim = r.screenForceLimit
      if (lim == null || lim === '' || Number.isNaN(Number(lim))) return 5
      const n = Number(lim)
      return Math.min(50, Math.max(1, n))
    },
    hasUnreadNotice() {
      if (!Array.isArray(this.notices) || this.notices.length === 0) return false
      return this.notices.some((n) => n && this.isNoticeUnread(n))
    },
    examHandUiMode() {
      if (!this.activeExam) return 'hidden'
      const s = String(this.examHandRemoteStatus || 'NONE').toUpperCase()
      if (s === 'RESPONDED') return 'responded'
      if (s === 'PENDING') return 'pending'
      return 'idle'
    },
    examWaitStartMs() {
      if (!this.examWaitContext || !this.examWaitContext.exam) return null
      const t = this.examWaitContext.exam.startTime
      const ms = new Date(String(t || '').replace(' ', 'T')).getTime()
      return Number.isFinite(ms) ? ms : null
    },
    examWaitEndMs() {
      if (!this.examWaitContext || !this.examWaitContext.exam) return null
      const t = this.examWaitContext.exam.endTime
      const ms = new Date(String(t || '').replace(' ', 'T')).getTime()
      return Number.isFinite(ms) ? ms : null
    },
    examWaitSecondsLeft() {
      const start = this.examWaitStartMs
      if (start == null) return 0
      void this.examWaitTickMs
      return Math.max(0, Math.ceil((start - Date.now()) / 1000))
    },
    examWaitCountdownText() {
      const s = Math.max(0, Number(this.examWaitSecondsLeft || 0))
      const h = String(Math.floor(s / 3600)).padStart(2, '0')
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
      const sec = String(s % 60).padStart(2, '0')
      return `${h}:${m}:${sec}`
    },
    /** 等待开考页：教师是否已点击「发布」 */
    examWaitPublishedOk() {
      if (!this.examWaitContext || !this.examWaitContext.exam) return false
      return this.isExamPublishedFlag(this.examWaitContext.exam)
    },
    examWaitCanEnterNow() {
      if (!this.examWaitContext) return false
      void this.examWaitTickMs
      if (!this.examWaitPublishedOk) return false
      const now = Date.now()
      const start = this.examWaitStartMs
      if (start != null && now < start) return false
      const end = this.examWaitEndMs
      if (end != null && now >= end) return false
      return true
    },
    examWaitPastEndNow() {
      const end = this.examWaitEndMs
      return end != null && Date.now() >= end
    }
  },
  created() {
    this.user = JSON.parse(localStorage.getItem('exam_user') || '{}')
    this.tryRestoreExamFromScratchPage()
    this.refreshNoticeSeenFromStorage()
    this.loadExamData()
    this.loadNotices()
  },
  mounted() {
    window.addEventListener('auth-changed', this.onStudentAuthChanged)
    this.noticeListPollTimer = setInterval(() => {
      this.loadNotices()
    }, 20000)
    try {
      const t = sessionStorage.getItem('student_open_tab')
      if (t === 'exam' || t === 'score') {
        this.currentTab = t
        sessionStorage.removeItem('student_open_tab')
      }
    } catch (e) {
      void e
    }
    this.maybeApplyScratchMergeFromPage()
  },
  beforeUnmount() {
    window.removeEventListener('auth-changed', this.onStudentAuthChanged)
    if (this.noticeListPollTimer) {
      clearInterval(this.noticeListPollTimer)
      this.noticeListPollTimer = null
    }
    this.clearExamTimers()
    if (this.restoreTipTimer) clearTimeout(this.restoreTipTimer)
    this.stopCamera()
    this.stopExamCameraMonitor()
    this.stopExamAntiCheat()
    this.stopExamBroadcastPoll()
    this.closeExamBroadcastModal(true)
    this.stopExamVoicePoll()
    this.teardownExamVoice()
    this.stopExamHandPoll()
    this.stopExamDmPoll()
    this.clearSubmitConfirmTimer()
    this.showSubmitConfirmModal = false
    this.cancelExamWait()
  },
  watch: {
    currentTab(v) {
      if (v === 'home') this.loadNotices()
      if (v !== 'exam' && this.examWaitContext) this.cancelExamWait()
    },
    currentQuestionIndex(newIdx, oldIdx) {
      this.scrollCurrentQuestionIntoView()
      if (this.activeExam && this.activeExam.record && this.activeExam.record.id) {
        this.saveExamProgress(this.activeExam.record.id)
      }
      if (oldIdx != null && oldIdx !== newIdx && Array.isArray(this.examQuestions) && this.examQuestions[oldIdx]) {
        const prevQ = this.examQuestions[oldIdx]
        const prevQid = this.normalizeQuestionId(prevQ.id)
        if (
          prevQid != null &&
          String(prevQ.type || '').toLowerCase() === 'coding' &&
          this.dirtyQuestionIds[prevQid]
        ) {
          this.flushCodingDraftForQuestion(prevQid)
        }
      }
    }
  },
  methods: {
    /** 与「在线考试」列表一致：无有效结束时间，或当前未超过结束时间，才展示 */
    examNotPastEnd(examOrEndTime) {
      const raw =
        examOrEndTime && typeof examOrEndTime === 'object' && 'endTime' in examOrEndTime
          ? examOrEndTime.endTime
          : examOrEndTime
      const now = Date.now()
      const end = new Date(String(raw || '').replace(' ', 'T')).getTime()
      return !end || now <= end
    },
    parseNoticeContent(raw) {
      const fallback = { text: String(raw || ''), assets: [] }
      if (!raw) return fallback
      try {
        const obj = JSON.parse(raw)
        if (!obj || typeof obj !== 'object') return fallback
        return {
          text: String(obj.text || ''),
          assets: Array.isArray(obj.assets) ? obj.assets : []
        }
      } catch (e) {
        return fallback
      }
    },
    noticePreviewText(row) {
      const parsed = this.parseNoticeContent(row && row.content)
      const text = parsed.text || ''
      const suffix = parsed.assets.length > 0 ? `（含${parsed.assets.length}个附件）` : ''
      if (text) return `${text.slice(0, 56)}${text.length > 56 ? '...' : ''}${suffix}`
      return suffix || '点击查看公告详情'
    },
    onStudentAuthChanged() {
      this.user = JSON.parse(localStorage.getItem('exam_user') || '{}')
      this.refreshNoticeSeenFromStorage()
    },
    noticeSeenStorageKey() {
      const id = this.user && this.user.id != null ? String(this.user.id) : String(this.user.username || 'guest')
      return `exam_student_notice_seen_rev:${id}`
    },
    refreshNoticeSeenFromStorage() {
      try {
        const raw = localStorage.getItem(this.noticeSeenStorageKey())
        const obj = raw ? JSON.parse(raw) : {}
        this.noticeSeenRevisions =
          obj && typeof obj === 'object' && !Array.isArray(obj) ? { ...obj } : {}
      } catch (e) {
        this.noticeSeenRevisions = {}
      }
    },
    noticeVersionToken(n) {
      if (!n || n.id == null) return ''
      const pick =
        n.updateTime != null && n.updateTime !== ''
          ? n.updateTime
          : n.publishTime != null && n.publishTime !== ''
            ? n.publishTime
            : n.createTime
      if (pick == null || pick === '') return ''
      if (Array.isArray(pick)) {
        const [y, mo, d, h, mi, s, ns] = pick
        const pad = (x) => String(x == null ? 0 : x).padStart(2, '0')
        if (y == null) return ''
        const sub = ns != null ? `.${String(ns).padStart(9, '0')}` : ''
        return `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:${pad(s)}${sub}`
      }
      return String(pick).trim().replace(' ', 'T')
    },
    parseNoticeTimeMs(token) {
      if (token == null || token === '') return NaN
      let s = String(token).replace(' ', 'T')
      let t = Date.parse(s)
      if (!Number.isFinite(t) && s.length === 16) t = Date.parse(`${s}:00`)
      return t
    },
    markNoticeAsRead(row) {
      if (!row || row.id == null) return
      const token = this.noticeVersionToken(row)
      const sid = String(row.id)
      const next = { ...this.noticeSeenRevisions, [sid]: token || 'seen' }
      this.noticeSeenRevisions = next
      try {
        localStorage.setItem(this.noticeSeenStorageKey(), JSON.stringify(next))
      } catch (e) {
        /* ignore quota / private mode */
      }
    },
    isNoticeUnread(n) {
      if (!n || n.id == null) return false
      const cur = this.noticeVersionToken(n)
      const sid = String(n.id)
      const seen = this.noticeSeenRevisions[sid]
      if (!cur) {
        return !seen
      }
      if (!seen) return true
      const a = this.parseNoticeTimeMs(cur)
      const b = this.parseNoticeTimeMs(seen)
      if (Number.isFinite(a) && Number.isFinite(b)) return a > b
      return cur !== seen
    },
    openNoticeModal(row) {
      if (row && row.id != null) this.markNoticeAsRead(row)
      this.currentNotice = row || {}
      this.currentNoticeParsed = this.parseNoticeContent((row && row.content) || '')
      this.showNoticeModal = true
    },
    closeNoticeModal() {
      this.showNoticeModal = false
      this.currentNotice = {}
      this.currentNoticeParsed = { text: '', assets: [] }
    },
    normalizeAssetUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (u.startsWith('http://') || u.startsWith('https://')) return u
      if (u.startsWith('/')) return u
      return `/${u}`
    },
    isImageAsset(asset) {
      const ct = String((asset && asset.contentType) || '').toLowerCase()
      return ct.startsWith('image/')
    },
    openAsset(asset) {
      const url = this.normalizeAssetUrl(asset && (asset.viewUrl || asset.downloadUrl))
      if (!url) return
      window.open(url, '_blank')
    },
    downloadAsset(asset) {
      const url = this.normalizeAssetUrl(asset && (asset.downloadUrl || asset.viewUrl))
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.download = asset && asset.originalName ? asset.originalName : 'notice-file'
      a.click()
    },
    loadNotices() {
      fetch('/api/crud/notice?page=0&size=20&status=1&sortBy=sort&sort=desc', {
        headers: this.authHeaders()
      })
        .then(async (r) => {
          if (!r.ok) {
            const err = new Error(`notice http ${r.status}`)
            err.status = r.status
            throw err
          }
          return r.json()
        })
        .then((res) => {
          this.notices = (res.data && res.data.content) || []
        })
        .catch(() => {
          this.notices = []
        })
    },
    enterExamFromHome(item) {
      if (!item || !item.record || !item.exam) {
        window.alert('当前考试信息不完整，暂时无法进入')
        return
      }
      this.currentTab = 'exam'
      this.openPrecheck({ record: item.record, exam: item.exam })
    },
    authHeaders(extra = {}) {
      const h = { 'X-Role': 'student', 'X-CSRF-Token': 'student-ui', ...extra }
      if (this.user && this.user.id != null) h['X-User-Id'] = String(this.user.id)
      return h
    },
    tryRestoreExamFromScratchPage() {
      try {
        const raw = sessionStorage.getItem('student_exam_scratch_return')
        if (!raw) return
        const o = JSON.parse(raw)
        if (!o || !o.record || !o.exam) return
        sessionStorage.removeItem('student_exam_scratch_return')
        this.currentTab = 'exam'
        this.pendingScratchQuestionIndex = Number(o.currentQuestionIndex) || 0
        this.resumeActiveExamAfterScratch({ record: o.record, exam: o.exam })
      } catch (e) {
        try {
          sessionStorage.removeItem('student_exam_scratch_return')
        } catch (e2) {
          void e2
        }
        void e
      }
    },
    resumeActiveExamAfterScratch(item) {
      this.activeExam = item
      this.showResumeTip = false
      this.restoredQuestionIndex = null
      const recordId = item.record.id
      this.restoreLocalAnswerSnapshot(recordId)
      this.loadPaperQuestionsForExam(item.exam.paperId, recordId)
      const endTs = new Date(String(item.exam.endTime || '').replace(' ', 'T')).getTime()
      this.startExamTimers(endTs, item.record.id)
      this.examHandRemoteStatus = 'NONE'
      this.examDmMessages = []
      this.examDmLastId = 0
      this.examDmInput = ''
      this.stopExamHandPoll()
      this.stopExamDmPoll()
      this.$nextTick(() => {
        this.startExamCameraMonitor(false)
        this.startExamCameraHealthCheck()
        this.startExamAntiCheat()
        this.startExamMonitorUpload()
        this.startExamBroadcastPoll()
        this.startExamVoicePoll()
        this.startExamHandPoll()
      })
    },
    async goStudentScratchExamPage() {
      if (!this.activeExam || !this.activeExam.record || !this.currentQuestion) return
      const q = this.currentQuestion
      if (String(q.type || '').toLowerCase() !== SCRATCH_TYPE) return
      await this.flushDirtyAnswersToServer(this.activeExam.record.id)
      this.saveExamProgress(this.activeExam.record.id)
      this.saveLocalAnswerSnapshot(this.activeExam.record.id)
      try {
        sessionStorage.setItem(
          'student_exam_scratch_return',
          JSON.stringify({
            record: this.activeExam.record,
            exam: this.activeExam.exam,
            currentQuestionIndex: this.currentQuestionIndex
          })
        )
        const scratchPrev = parseScratchAnswer(this.getAnswerForQuestion(q.id))
        let resumeProjectUrl = String(scratchPrev.viewUrl || scratchPrev.downloadUrl || '').trim()
        // 文件名含 recordId_questionId_，防止串题后把别的题的 sb3 带进上下文
        const sn = String(scratchPrev.storageName || '')
        const expectPrefix = `${this.activeExam.record.id}_${q.id}_`
        if (sn && !sn.startsWith(expectPrefix)) {
          resumeProjectUrl = ''
        }
        sessionStorage.setItem(
          'scratch_exam_context',
          JSON.stringify({
            recordId: this.activeExam.record.id,
            questionId: q.id,
            title: q.title || '',
            analysis: q.analysis || '',
            resumeProjectUrl
          })
        )
      } catch (e) {
        void e
      }
      // Scratch 内核挂在 window 上，SPA 二次进入会残留上一题工程；已加载过则整页冷启动
      if (typeof window !== 'undefined' && window.__examScratchCoreLoaded) {
        window.location.hash = '#/student/scratch-exam'
        window.location.reload()
        return
      }
      this.$router.push({ path: '/student/scratch-exam' })
    },
    maybeApplyScratchMergeFromPage() {
      try {
        const raw = sessionStorage.getItem('scratch_answer_merge')
        if (!raw) return
        const o = JSON.parse(raw)
        sessionStorage.removeItem('scratch_answer_merge')
        if (o && o.questionId != null && o.data) {
          this.onScratchUploaded(o.questionId, o.data)
        }
      } catch (e) {
        try {
          sessionStorage.removeItem('scratch_answer_merge')
        } catch (e2) {
          void e2
        }
        void e
      }
    },
    clearExamTimers() {
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      if (this.autosaveTimer) clearInterval(this.autosaveTimer)
      this.countdownTimer = null
      this.autosaveTimer = null
    },
    reportCameraAnomaly(type, detail) {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.record.id) return
      const now = Date.now()
      const heavyTypes = ['CAM_OCCUPIED', 'CAM_HEALTH_FAIL']
      const tenMinMs = 10 * 60 * 1000
      if (heavyTypes.includes(type)) {
        const lastH = this.cameraHeavyReportAt[type] || 0
        if (now - lastH < tenMinMs) return
        this.cameraHeavyReportAt[type] = now
      } else if (this.lastCameraAnomalyType === type && now - this.lastCameraAnomalyAt < 15000) {
        return
      }
      this.lastCameraAnomalyType = type
      this.lastCameraAnomalyAt = now
      const payload = {
        recordId: this.activeExam.record.id,
        userId: this.user.id,
        type: String(type).slice(0, 80),
        detail: detail ? String(detail).slice(0, 400) : ''
      }
      fetch('/api/student/exam/violation', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      }).catch(() => {})
    },
    startExamCameraMonitor(isAutoRetry = false) {
      if (!this.activeExam) return
      if (this.isAutomanLoggedInUser) {
        this.stopExamCameraStreamOnly()
        this.examCameraReady = false
        this.examCameraStatusText = 'Automan 账号免摄像头监控'
        return
      }
      if (!window.isSecureContext) {
        this.examCameraReady = false
        this.examCameraStatusText = '监控失败：请使用 HTTPS 或 localhost 访问。'
        this.reportCameraAnomaly('CAM_UNSAFE_CTX', 'not secure context')
        return
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.examCameraReady = false
        this.examCameraStatusText = '监控失败：浏览器不支持摄像头接口。'
        this.reportCameraAnomaly('CAM_UNSUPPORTED', 'getUserMedia unavailable')
        return
      }
      navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then((stream) => {
          this.stopExamCameraStreamOnly()
          this.examCameraStream = stream
          this.examCameraReady = true
          this.examCameraStatusText = '监控中：摄像头正常'
          this.$nextTick(() => {
            const video = this.$refs.examMonitorVideo
            if (video) video.srcObject = stream
          })
          const tracks = stream.getVideoTracks ? stream.getVideoTracks() : []
          if (tracks.length > 0) {
            tracks[0].onended = () => {
              this.examCameraReady = false
              this.examCameraStatusText = '监控中断：摄像头连接已断开，请重试开启。'
              this.reportCameraAnomaly('CAM_STREAM_ENDED', 'track ended')
            }
          }
        })
        .catch((err) => {
          this.examCameraReady = false
          const name = err && err.name ? err.name : ''
          if (name === 'NotReadableError') {
            this.examCameraStatusText = '监控失败：摄像头被其他程序占用，请关闭占用程序后重试。'
            this.reportCameraAnomaly('CAM_OCCUPIED', name)
          } else if (name === 'NotAllowedError') {
            this.examCameraStatusText = '监控失败：摄像头权限被拒绝，请在浏览器中允许权限。'
            this.reportCameraAnomaly('CAM_DENIED', name)
          } else if (name === 'NotFoundError') {
            this.examCameraStatusText = '监控失败：未检测到摄像头设备。'
            this.reportCameraAnomaly('CAM_NOT_FOUND', name)
          } else {
            this.examCameraStatusText = `监控失败：${name || '未知错误'}。`
            this.reportCameraAnomaly('CAM_ERROR', name || 'unknown')
          }
          if (isAutoRetry) return
        })
    },
    stopExamCameraStreamOnly() {
      if (this.examCameraStream && this.examCameraStream.getTracks) {
        this.examCameraStream.getTracks().forEach((t) => t.stop())
      }
      this.examCameraStream = null
    },
    startExamCameraHealthCheck() {
      if (this.examCameraMonitorTimer) clearInterval(this.examCameraMonitorTimer)
      this.examCameraMonitorTimer = null
      if (this.isAutomanLoggedInUser) return
      this.examCameraMonitorTimer = setInterval(() => {
        if (!this.activeExam) return
        const tracks = this.examCameraStream && this.examCameraStream.getVideoTracks
          ? this.examCameraStream.getVideoTracks()
          : []
        const healthy = tracks.length > 0 && tracks[0].readyState === 'live'
        if (!healthy) {
          this.examCameraReady = false
          this.examCameraStatusText = '监控异常：尝试自动恢复中...'
          this.reportCameraAnomaly('CAM_HEALTH_FAIL', 'track not live')
          this.startExamCameraMonitor(true)
        }
      }, 5000)
    },
    stopExamMonitorUpload() {
      if (this.examMonitorUploadTimer) clearInterval(this.examMonitorUploadTimer)
      this.examMonitorUploadTimer = null
    },
    startExamMonitorUpload() {
      this.stopExamMonitorUpload()
      if (!this.activeExam || !this.user.id) return
      if (this.isAutomanLoggedInUser) return
      // Real-time proctoring: ~4s/frame (阶段1容量优化，见 docs/ECS并发考试容量分析与优化建议.md)
      this.examMonitorUploadTimer = setInterval(() => this.uploadExamMonitorFrame(), 4000)
      setTimeout(() => this.uploadExamMonitorFrame(), 1200)
    },
    uploadExamMonitorFrame() {
      if (!this.activeExam || !this.user.id) return
      const video = this.$refs.examMonitorVideo
      if (!video || !video.videoWidth) return
      try {
        const canvas = document.createElement('canvas')
        const maxW = 420
        const vw = video.videoWidth
        const vh = video.videoHeight
        const w = Math.min(maxW, vw)
        const h = Math.round((w * vh) / vw)
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(video, 0, 0, w, h)
        const imageBase64 = canvas.toDataURL('image/jpeg', 0.38)
        if (!imageBase64 || imageBase64.length < 120) return
        fetch('/api/student/exam/monitor-frame', {
          method: 'POST',
          headers: this.authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            recordId: this.activeExam.record.id,
            userId: this.user.id,
            imageBase64
          })
        }).catch(() => {})
      } catch (e) {
        void e
      }
    },
    canvasToCompressedJpegDataUrl(canvas, maxWidth = 960, quality = 0.72) {
      if (!canvas || !canvas.width || !canvas.height) return ''
      const srcW = canvas.width
      const srcH = canvas.height
      const targetW = Math.min(maxWidth, srcW)
      const targetH = Math.round((targetW * srcH) / srcW)
      const targetCanvas = document.createElement('canvas')
      targetCanvas.width = targetW
      targetCanvas.height = targetH
      const ctx = targetCanvas.getContext('2d')
      if (!ctx) return ''
      ctx.drawImage(canvas, 0, 0, targetW, targetH)
      return targetCanvas.toDataURL('image/jpeg', quality)
    },
    compressImageDataUrl(dataUrl, maxWidth = 960, quality = 0.72) {
      return new Promise((resolve) => {
        if (!dataUrl) {
          resolve('')
          return
        }
        const img = new Image()
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            if (!ctx) {
              resolve(String(dataUrl || ''))
              return
            }
            ctx.drawImage(img, 0, 0, img.width, img.height)
            resolve(this.canvasToCompressedJpegDataUrl(canvas, maxWidth, quality) || String(dataUrl || ''))
          } catch (e) {
            resolve(String(dataUrl || ''))
          }
        }
        img.onerror = () => resolve(String(dataUrl || ''))
        img.src = dataUrl
      })
    },
    isExamAnticheatMuted() {
      return Date.now() < this.examAnticheatMutedUntil
    },
    startExamBroadcastPoll() {
      this.stopExamBroadcastPoll()
      if (!this.activeExam || !this.activeExam.exam) return
      this.broadcastPollAfterId = 0
      this.examBroadcastQueue = []
      this.examBroadcastPollTimer = setInterval(() => this.pollExamBroadcast(), 3000)
      setTimeout(() => this.pollExamBroadcast(), 600)
    },
    stopExamBroadcastPoll() {
      if (this.examBroadcastPollTimer) {
        clearInterval(this.examBroadcastPollTimer)
        this.examBroadcastPollTimer = null
      }
    },
    pollExamBroadcast() {
      if (!this.activeExam || !this.activeExam.exam || !this.user.id) return
      const eid = this.activeExam.exam.id
      fetch(
        `/api/student/exam-broadcast/pending?examId=${eid}&afterId=${this.broadcastPollAfterId}`,
        { headers: this.authHeaders() }
      )
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !res.data) return
          this.mergeIncomingBroadcasts(res.data)
        })
        .catch(() => {})
    },
    mergeIncomingBroadcasts(items) {
      if (!items || !items.length) return
      let maxA = this.broadcastPollAfterId
      for (const it of items) {
        const id = Number(it.id)
        if (!Number.isFinite(id)) continue
        if (id > maxA) maxA = id
        if (this.examBroadcastCurrentId != null && id === this.examBroadcastCurrentId) continue
        if (this.examBroadcastQueue.some((x) => Number(x.id) === id)) continue
        this.examBroadcastQueue.push({
          id,
          content: String(it.content == null ? '' : it.content)
        })
      }
      this.examBroadcastQueue.sort((a, b) => a.id - b.id)
      this.broadcastPollAfterId = maxA
      this.flushExamBroadcastQueue()
    },
    flushExamBroadcastQueue() {
      if (this.showExamBroadcastModal) return
      if (!this.examBroadcastQueue.length) return
      const next = this.examBroadcastQueue.shift()
      this.openExamBroadcastOverlay(next)
    },
    openExamBroadcastOverlay(item) {
      if (!item || item.id == null) return
      this.examBroadcastCurrentId = item.id
      this.examBroadcastContent = item.content || ''
      this.showExamBroadcastModal = true
      this.examBroadcastRemainSeconds = 60
      this.examAnticheatMutedUntil = Math.max(this.examAnticheatMutedUntil, Date.now() + 95000)
      if (this.examBroadcastTickTimer) clearInterval(this.examBroadcastTickTimer)
      this.examBroadcastTickTimer = setInterval(() => {
        this.examBroadcastRemainSeconds -= 1
        if (this.examBroadcastRemainSeconds <= 0) this.closeExamBroadcastModal()
      }, 1000)
    },
    closeExamBroadcastModal(arg) {
      const fromUnmount = arg === true
      if (this.examBroadcastTickTimer) {
        clearInterval(this.examBroadcastTickTimer)
        this.examBroadcastTickTimer = null
      }
      this.showExamBroadcastModal = false
      this.examBroadcastCurrentId = null
      if (fromUnmount) return
      this.examAnticheatMutedUntil = Math.max(this.examAnticheatMutedUntil, Date.now() + 15000)
      this.$nextTick(() => this.flushExamBroadcastQueue())
    },
    startExamVoicePoll() {
      this.stopExamVoicePoll()
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      this.examVoiceLastPullId = 0
      this.scheduleExamVoicePoll()
    },
    scheduleExamVoicePoll() {
      if (this.examVoicePollTimer) {
        clearTimeout(this.examVoicePollTimer)
        this.examVoicePollTimer = null
      }
      const delay = (this.examVoicePc || this.examVoiceLocalStream) ? 800 : 2500
      this.examVoicePollTimer = setTimeout(() => {
        this.pollExamVoice()
        if (this.activeExam && this.activeExam.record && this.activeExam.exam) {
          this.scheduleExamVoicePoll()
        }
      }, delay)
    },
    stopExamVoicePoll() {
      if (this.examVoicePullTimer) {
        clearInterval(this.examVoicePullTimer)
        this.examVoicePullTimer = null
      }
      if (this.examVoicePollTimer) {
        clearTimeout(this.examVoicePollTimer)
        this.examVoicePollTimer = null
      }
    },
    pollExamVoice() {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      const examId = this.activeExam.exam.id
      const recordId = this.activeExam.record.id
      fetch(
        `/api/student/exam-voice/signal?examId=${examId}&recordId=${recordId}&afterId=${this.examVoiceLastPullId}`,
        { headers: this.authHeaders() }
      )
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !res.data) return
          let maxId = this.examVoiceLastPullId
          for (const row of res.data) {
            const id = Number(row.id)
            if (Number.isFinite(id) && id > maxId) maxId = id
            this.applyExamVoiceRow(row)
          }
          this.examVoiceLastPullId = maxId
        })
        .catch(() => {})
    },
    applyExamVoiceRow(row) {
      const t = String(row.signalType || '')
      const raw = String(row.payload || '{}')
      if (t === 'hangup') {
        this.teardownExamVoice()
        return
      }
      if (t === 'offer') {
        this.handleTeacherVoiceOffer(raw).catch(() => {})
        return
      }
      if (t === 'candidate') {
        try {
          const j = JSON.parse(raw)
          const cand = new RTCIceCandidate(j)
          const pc = this.examVoicePc
          if (!pc) return
          if (!pc.remoteDescription) {
            this.examVoiceTeacherIceQueue.push(cand)
            return
          }
          pc.addIceCandidate(cand).catch(() => {})
        } catch (e) {
          void e
        }
      }
    },
    async handleTeacherVoiceOffer(raw) {
      if (!window.RTCPeerConnection || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return
      let sdp = ''
      try {
        const j = JSON.parse(raw)
        sdp = j && j.sdp ? String(j.sdp) : ''
      } catch (e) {
        return
      }
      if (!sdp) return
      await this.teardownExamVoice()
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      })
      this.examVoicePc = pc
      pc.ontrack = (ev) => {
        const el = this.$refs.examVoiceRemoteAudio
        if (el && ev.streams && ev.streams[0]) {
          el.srcObject = ev.streams[0]
        }
      }
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      } catch (e) {
        window.alert('监考教师发起语音通话，需要您允许使用麦克风。请在浏览器中授权后刷新页面或等待教师再次发起。')
        throw e
      }
      this.examVoiceLocalStream = stream
      stream.getTracks().forEach((track) => pc.addTrack(track, stream))
      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          this.pushStudentVoiceSignal('candidate', JSON.stringify(ev.candidate.toJSON())).catch(() => {})
        }
      }
      await pc.setRemoteDescription({ type: 'offer', sdp })
      const answer = await pc.createAnswer()
      await pc.setLocalDescription(answer)
      await this.pushStudentVoiceSignal('answer', JSON.stringify({ type: 'answer', sdp: answer.sdp }))
      const queued = this.examVoiceTeacherIceQueue.splice(0)
      for (const c of queued) {
        try {
          await pc.addIceCandidate(c)
        } catch (e) {
          void e
        }
      }
    },
    async pushStudentVoiceSignal(signalType, payloadStr) {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      await fetch('/api/student/exam-voice/signal', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          examId: this.activeExam.exam.id,
          recordId: this.activeExam.record.id,
          signalType,
          payload: payloadStr
        })
      })
    },
    teardownExamVoice() {
      if (this.examVoicePc) {
        try {
          this.examVoicePc.close()
        } catch (e) {
          void e
        }
        this.examVoicePc = null
      }
      if (this.examVoiceLocalStream) {
        this.examVoiceLocalStream.getTracks().forEach((t) => t.stop())
        this.examVoiceLocalStream = null
      }
      const el = this.$refs.examVoiceRemoteAudio
      if (el) el.srcObject = null
      this.examVoiceTeacherIceQueue = []
    },
    stopExamHandPoll() {
      if (this.examHandPollTimer) {
        clearInterval(this.examHandPollTimer)
        this.examHandPollTimer = null
      }
    },
    startExamHandPoll() {
      this.stopExamHandPoll()
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      this.pollExamHandOnce()
      this.examHandPollTimer = setInterval(() => this.pollExamHandOnce(), 2000)
    },
    pollExamHandOnce() {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      const examId = this.activeExam.exam.id
      const recordId = this.activeExam.record.id
      const prev = String(this.examHandRemoteStatus || 'NONE').toUpperCase()
      fetch(`/api/student/exam-hand/status?examId=${examId}&recordId=${recordId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !res.data) return
          const st = String(res.data.status || 'NONE').toUpperCase()
          if (prev === 'RESPONDED' && st === 'ENDED') {
            this.stopExamDmPoll()
            this.examDmMessages = []
            this.examDmLastId = 0
            this.examDmInput = ''
            this.teardownExamVoice()
          }
          this.examHandRemoteStatus = st
          if (st === 'RESPONDED') {
            if (prev !== 'RESPONDED') {
              this.examDmLastId = 0
              this.examDmMessages = []
            }
            if (!this.examDmPollTimer) this.startExamDmPoll()
          } else {
            this.stopExamDmPoll()
            if (st !== 'ENDED' && st !== 'RESPONDED') {
              this.examDmMessages = []
              this.examDmLastId = 0
            }
          }
        })
        .catch(() => {})
    },
    studentRaiseHand() {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      fetch('/api/student/exam-hand/raise', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          examId: this.activeExam.exam.id,
          recordId: this.activeExam.record.id
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '举手失败')
            return
          }
          this.examHandRemoteStatus = 'PENDING'
        })
        .catch(() => window.alert('网络错误'))
    },
    studentCancelRaiseHand() {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      fetch('/api/student/exam-hand/cancel', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          examId: this.activeExam.exam.id,
          recordId: this.activeExam.record.id
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '取消失败')
            return
          }
          this.examHandRemoteStatus = 'NONE'
        })
        .catch(() => window.alert('网络错误'))
    },
    stopExamDmPoll() {
      if (this.examDmPollTimer) {
        clearInterval(this.examDmPollTimer)
        this.examDmPollTimer = null
      }
    },
    startExamDmPoll() {
      if (String(this.examHandRemoteStatus || '').toUpperCase() !== 'RESPONDED') return
      this.stopExamDmPoll()
      this.pollExamDmOnce()
      this.examDmPollTimer = setInterval(() => this.pollExamDmOnce(), 2000)
    },
    pollExamDmOnce() {
      if (!this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      if (String(this.examHandRemoteStatus || '').toUpperCase() !== 'RESPONDED') return
      const examId = this.activeExam.exam.id
      const recordId = this.activeExam.record.id
      fetch(
        `/api/student/exam-dm/messages?examId=${examId}&recordId=${recordId}&afterId=${this.examDmLastId}`,
        { headers: this.authHeaders() }
      )
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !Array.isArray(res.data)) return
          let maxId = this.examDmLastId
          for (const row of res.data) {
            const id = Number(row.id)
            if (!Number.isFinite(id)) continue
            if (this.examDmMessages.some((x) => x.id === id)) continue
            if (id > maxId) maxId = id
            this.examDmMessages.push({
              id,
              fromRole: String(row.fromRole || ''),
              content: String(row.content || '')
            })
          }
          this.examDmLastId = maxId
          this.$nextTick(() => {
            const el = this.$refs.examDmScroll
            if (el) el.scrollTop = el.scrollHeight
          })
        })
        .catch(() => {})
    },
    sendStudentDm() {
      const t = String(this.examDmInput || '').trim()
      if (!t || !this.activeExam || !this.activeExam.record || !this.activeExam.exam) return
      if (String(this.examHandRemoteStatus || '').toUpperCase() !== 'RESPONDED') return
      this.examDmSending = true
      fetch('/api/student/exam-dm/send', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          examId: this.activeExam.exam.id,
          recordId: this.activeExam.record.id,
          content: t
        })
      })
        .then((r) => r.json())
        .then((res) => {
          this.examDmSending = false
          if (res.code !== 200) {
            window.alert(res.message || '发送失败')
            return
          }
          this.examDmInput = ''
          this.pollExamDmOnce()
        })
        .catch(() => {
          this.examDmSending = false
          window.alert('网络错误')
        })
    },
    stopExamCameraMonitor() {
      this.stopExamMonitorUpload()
      if (this.examCameraMonitorTimer) clearInterval(this.examCameraMonitorTimer)
      this.examCameraMonitorTimer = null
      this.stopExamCameraStreamOnly()
      this.examCameraReady = false
      this.examCameraStatusText = '监控未开启'
      this.cameraHeavyReportAt = {}
    },
    displayStatus(status) {
      const s = String(status || '').toUpperCase()
      if (s === 'NOT_START') return '未开始'
      if (s === 'ING') return '进行中'
      if (s === 'END') return '已结束'
      return status || '-'
    },
    isExamPublishedFlag(exam) {
      const p = exam && exam.published
      return Number(p) === 1 || p === true || String(p) === '1'
    },
    /** 仅按开考/结束时间推导阶段（与后端 refresh 规则一致）；END 终态保留 */
    effectiveExamPhase(exam) {
      if (!exam) return 'NOT_START'
      const raw = String(exam.status || '').toUpperCase()
      if (raw === 'END') return 'END'
      const startMs = new Date(String(exam.startTime || '').replace(' ', 'T')).getTime()
      const endMs = new Date(String(exam.endTime || '').replace(' ', 'T')).getTime()
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return raw || 'NOT_START'
      const now = Date.now()
      if (now < startMs) return 'NOT_START'
      if (now >= endMs) return 'END'
      return 'ING'
    },
    /** 列表「状态」列：未发布优先显示「待发布」，否则显示时间推导后的阶段 */
    displayExamStatusLabel(exam) {
      if (!exam) return '-'
      if (!this.isExamPublishedFlag(exam)) {
        const phase = this.effectiveExamPhase(exam)
        if (phase === 'END') return '已结束'
        return '待发布'
      }
      return this.displayStatus(this.effectiveExamPhase(exam))
    },
    loadExamData() {
      if (!this.user.id) return Promise.resolve()
      return fetch(
        `/api/crud/exam-record?page=0&size=500&userId=${this.user.id}&sortBy=id&sort=desc`,
        { headers: this.authHeaders() }
      )
        .then(async (r) => {
          if (!r.ok) throw new Error(`exam-record http ${r.status}`)
          return r.json()
        })
        .then((res) => {
          this.examRecords = (res.data && res.data.content) || []
          return fetch('/api/crud/exam?page=0&size=500&sortBy=id&sort=desc', {
            headers: this.authHeaders()
          })
        })
        .then(async (r) => {
          if (!r.ok) throw new Error(`exam http ${r.status}`)
          return r.json()
        })
        .then((res) => {
          this.exams = (res.data && res.data.content) || []
        })
        .catch(() => {
          this.examRecords = []
          this.exams = []
        })
    },
    openScoreTab() {
      this.currentTab = 'score'
      this.loadMyScores()
    },
    goMoreInfo() {
      window.location.hash = '#/student/more-info'
    },
    loadMyScores() {
      if (!this.user.id) return
      fetch('/api/student/exam/scores/mine', {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.scoreRows = res.data || []
            return
          }
          this.scoreRows = []
          window.alert(res.message || '成绩加载失败')
        })
        .catch(() => {
          this.scoreRows = []
          window.alert('成绩加载失败')
        })
    },
    openPrecheck(item) {
      if (!item || !item.record || !item.exam) {
        window.alert('当前考试信息不完整，暂时无法进入')
        return
      }
      const recordId = item.record.id
      const uid = this.user && this.user.id
      if (recordId == null || !uid) {
        this.beginPrecheckWithRecord(item)
        return
      }
      fetch(
        `/api/crud/exam-record?page=0&size=500&userId=${uid}&sortBy=id&sort=desc`,
        { headers: this.authHeaders() }
      )
        .then(async (r) => {
          if (!r.ok) {
            const msg = r.status === 502
              ? '后端服务暂不可用(502)，请稍后再试或联系监考老师'
              : `获取答卷失败(HTTP ${r.status})`
            throw new Error(msg)
          }
          return r.json()
        })
        .then((res) => {
          const list = (res.data && res.data.content) || []
          const fresh = list.find((x) => String(x.id) === String(recordId))
          if (!fresh) {
            window.alert('未找到本场考试的答卷记录，请返回首页点击「刷新」后再试。')
            return
          }
          const has = this.examRecords.some((x) => String(x.id) === String(recordId))
          this.examRecords = has
            ? this.examRecords.map((r) => (String(r.id) === String(recordId) ? fresh : r))
            : [...this.examRecords, fresh]
          this.beginPrecheckWithRecord({ ...item, record: fresh })
        })
        .catch((e) => {
          window.alert((e && e.message) || '获取答卷最新状态失败，请检查网络后重试')
        })
    },
    beginPrecheckWithRecord(item) {
      if (item && item.record) {
        const st = String(item.record.status || '').toUpperCase()
        if (st === 'COMMIT' || st === 'FORCE_END' || st === 'GRADED') {
          window.alert(
            '当前答卷为已交卷、强制交卷或已阅卷状态，暂不可进入作答。\n\n' +
              '若教师已在「考生试卷管理」中为您点击「打回继续作答」，答卷会恢复为可作答状态。请再点一次「进入考试」或先点击「刷新」列表后再进入（进入前系统会自动从服务器同步最新状态）。'
          )
          return
        }
      }
      // Automan 登录学员跳过网络/摄像头/麦克风/键鼠四步核验
      if (this.isAutomanLoggedInUser) {
        this.skipPrecheckForAutoman(item)
        return
      }
      this.precheckContext = item
      this.networkOk = false
      this.networkStatusText = '未检测'
      this.networkLatencyMs = null
      this.cameraReady = false
      this.photoDataUrl = ''
      this.cameraError = ''
      this.deviceCheckAnswer = ''
      this.mouseOk = false
      this.precheckSaveOkMessage = ''
      this.resetPrecheckMicState()
      this.stopCamera()
    },
    skipPrecheckForAutoman(item) {
      if (!item || !item.exam || !item.record || !this.user || !this.user.id) {
        window.alert('当前考试信息不完整，暂时无法进入')
        return
      }
      const payload = {
        examId: item.exam.id,
        recordId: item.record.id,
        userId: this.user.id,
        networkStatus: 'SKIPPED_AUTOMAN',
        networkLatencyMs: 0,
        cameraPhoto: '',
        microphoneStatus: 'SKIPPED_AUTOMAN',
        keyboardStatus: 'SKIPPED_AUTOMAN',
        mouseStatus: 'SKIPPED_AUTOMAN',
        simpleAnswer: 'AUTOMAN_SKIP',
        status: 'PASS'
      }
      fetch('/api/crud/exam-entry-check', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) {
            throw new Error((res && res.message) || '进入考试失败')
          }
          const endMs = new Date(String(item.exam.endTime || '').replace(' ', 'T')).getTime()
          if (endMs && Number.isFinite(endMs) && Date.now() >= endMs) {
            window.alert('考试已结束，无法进入。')
            return
          }
          this.doEnterExam(item)
        })
        .catch((e) => {
          window.alert(e.message || '进入考试失败，请重试')
        })
    },
    cancelPrecheck() {
      this.precheckContext = null
      this.precheckSaveOkMessage = ''
      this.resetPrecheckMicState()
      this.stopCamera()
    },
    resetPrecheckMicState() {
      this.stopPrecheckMicStream()
      this.precheckMicStatus = ''
      this.micFailAcknowledged = false
    },
    stopPrecheckMicStream() {
      if (this.precheckMicStream && this.precheckMicStream.getTracks) {
        this.precheckMicStream.getTracks().forEach((t) => t.stop())
      }
      this.precheckMicStream = null
    },
    testPrecheckMicrophone() {
      if (!this.photoDataUrl) return
      this.stopPrecheckMicStream()
      this.micFailAcknowledged = false
      if (!window.isSecureContext) {
        this.precheckMicStatus = 'fail'
        return
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.precheckMicStatus = 'fail'
        return
      }
      this.precheckMicStatus = 'testing'
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          this.precheckMicStream = stream
          this.precheckMicStatus = 'ok'
          stream.getTracks().forEach((t) => t.stop())
          this.precheckMicStream = null
        })
        .catch(() => {
          this.precheckMicStatus = 'fail'
        })
    },
    ackPrecheckMicFail() {
      this.micFailAcknowledged = true
    },
    testNetwork() {
      const started = Date.now()
      fetch(`/api/crud/exam?page=0&size=1&_t=${started}`)
        .then((r) => r.json())
        .then(() => {
          const cost = Date.now() - started
          this.networkLatencyMs = cost
          this.networkOk = true
          this.networkStatusText = `网络正常（延迟约 ${cost} ms）`
        })
        .catch(() => {
          this.networkOk = false
          this.networkStatusText = '网络异常，请检查后重试'
        })
    },
    startCamera() {
      if (!this.networkOk) return
      this.cameraError = ''
      if (!window.isSecureContext) {
        this.cameraReady = false
        this.cameraError = '当前页面不是安全上下文，摄像头通常仅支持 HTTPS 或 localhost。请使用 http://localhost 访问。'
        return
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraReady = false
        this.cameraError = '当前浏览器不支持摄像头接口，请更换 Chrome/Edge 新版或使用上传照片。'
        return
      }
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          this.cameraStream = stream
          this.cameraReady = true
          this.$nextTick(() => {
            const video = this.$refs.cameraVideo
            if (video) video.srcObject = stream
          })
        })
        .catch((err) => {
          this.cameraReady = false
          const name = err && err.name ? err.name : ''
          if (name === 'NotAllowedError') {
            this.cameraError = '摄像头权限被拒绝，请在浏览器地址栏中允许摄像头权限后重试。'
          } else if (name === 'NotFoundError') {
            this.cameraError = '未检测到可用摄像头设备，请连接摄像头或使用上传照片。'
          } else if (name === 'NotReadableError') {
            this.cameraError = '摄像头被其他程序占用，请关闭占用程序后重试。'
          } else {
            this.cameraError = `无法调用摄像头（${name || '未知错误'}），请检查权限或使用上传照片。`
          }
        })
    },
    stopCamera() {
      if (this.cameraStream && this.cameraStream.getTracks) {
        this.cameraStream.getTracks().forEach((t) => t.stop())
      }
      this.cameraStream = null
      this.cameraReady = false
    },
    capturePhoto() {
      const video = this.$refs.cameraVideo
      if (!video || !video.videoWidth || !video.videoHeight) {
        window.alert('摄像头尚未就绪，请稍后重试')
        return
      }
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      this.photoDataUrl = this.canvasToCompressedJpegDataUrl(canvas, 1080, 0.82)
      this.stopCamera()
    },
    retakePhoto() {
      this.photoDataUrl = ''
      this.resetPrecheckMicState()
      this.deviceCheckAnswer = ''
      this.mouseOk = false
      this.startCamera()
    },
    onPhotoUpload(e) {
      const files = e && e.target && e.target.files ? e.target.files : []
      const file = files.length > 0 ? files[0] : null
      if (!file) return
      const reader = new FileReader()
      reader.onload = async () => {
        const raw = String(reader.result || '')
        this.photoDataUrl = await this.compressImageDataUrl(raw, 1080, 0.82)
        this.stopCamera()
        this.cameraError = ''
      }
      reader.onerror = () => {
        window.alert('读取图片失败，请重试')
      }
      reader.readAsDataURL(file)
      if (this.$refs.photoUploadInput) this.$refs.photoUploadInput.value = ''
    },
    confirmMouseClick() {
      this.mouseOk = true
    },
    finishPrecheckAndEnter() {
      if (!this.precheckContext) return
      if (!this.canEnterExam) {
        window.alert('请先完成考前核验（网络、拍照、麦克风、键鼠共四步）')
        return
      }
      const item = this.precheckContext
      const payload = {
        examId: item.exam.id,
        recordId: item.record.id,
        userId: this.user.id,
        networkStatus: this.networkOk ? 'OK' : 'FAIL',
        networkLatencyMs: this.networkLatencyMs || 0,
        cameraPhoto: this.photoDataUrl,
        microphoneStatus:
          this.precheckMicStatus === 'ok' ? 'OK' : this.micFailAcknowledged ? 'UNAVAILABLE_ACK' : 'UNKNOWN',
        keyboardStatus: this.keyboardOk ? 'OK' : 'FAIL',
        mouseStatus: this.mouseOk ? 'OK' : 'FAIL',
        simpleAnswer: this.deviceCheckAnswer,
        status: 'PASS'
      }
      fetch('/api/crud/exam-entry-check', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) {
            throw new Error((res && res.message) || '核验信息保存失败')
          }
          this.precheckContext = null
          this.resetPrecheckMicState()
          this.stopCamera()
          const endMs = new Date(String(item.exam.endTime || '').replace(' ', 'T')).getTime()
          if (endMs && Number.isFinite(endMs) && Date.now() >= endMs) {
            window.alert('考试已结束，无法进入。')
            return
          }
          const startMs = new Date(String(item.exam.startTime || '').replace(' ', 'T')).getTime()
          const willWait = Number.isFinite(startMs) && Date.now() < startMs
          this.precheckSaveOkMessage = willWait
            ? '核验信息已保存成功，请在下方等候区等待开考后再进入答题。'
            : '核验信息已保存成功，正在进入考试...'
          setTimeout(() => {
            this.precheckSaveOkMessage = ''
          }, willWait ? 3200 : 1800)
          this.doEnterExam(item)
        })
        .catch((e) => {
          window.alert(e.message || '核验信息保存失败，请重试')
        })
    },
    startExamWaitTimer() {
      this.clearExamWaitTimer()
      this.examWaitTickMs = Date.now()
      this.examWaitTimer = setInterval(() => {
        if (!this.examWaitContext) {
          this.clearExamWaitTimer()
          return
        }
        this.examWaitTickMs = Date.now()
        const end = this.examWaitEndMs
        if (end != null && Date.now() >= end) {
          window.alert('考试已结束，无法进入。')
          this.cancelExamWait()
          return
        }
        if (this.examWaitCanEnterNow) {
          this.tryEnterFromWait()
        }
      }, 1000)
    },
    clearExamWaitTimer() {
      if (this.examWaitTimer) {
        clearInterval(this.examWaitTimer)
        this.examWaitTimer = null
      }
    },
    cancelExamWait() {
      this.clearExamWaitTimer()
      this.examWaitContext = null
    },
    tryEnterFromWait() {
      const ctx = this.examWaitContext
      if (!ctx || !ctx.record || !ctx.exam) return
      const now = Date.now()
      const end = new Date(String(ctx.exam.endTime || '').replace(' ', 'T')).getTime()
      if (end && Number.isFinite(end) && now >= end) {
        window.alert('考试已结束，无法进入。')
        this.cancelExamWait()
        return
      }
      const item = { record: ctx.record, exam: ctx.exam }
      this.clearExamWaitTimer()
      this.examWaitContext = null
      this.doEnterExam(item)
    },
    async doEnterExam(item) {
      const ok = await this.ensureSingleScreenBeforeExam(item)
      if (!ok) return
      this.doEnterExamProceed(item)
    },
    doEnterExamProceed(item) {
      if (!this.isExamPublishedFlag(item.exam)) {
        window.alert(
          '教师尚未发布本场考试，暂时无法进入答题。请联系监考老师在「考试管理」中点击「发布」，然后在本页点击「刷新」。'
        )
        return
      }
      const now = Date.now()
      const start = new Date(String(item.exam.startTime || '').replace(' ', 'T')).getTime()
      const end = new Date(String(item.exam.endTime || '').replace(' ', 'T')).getTime()
      if (Number.isFinite(end) && now >= end) {
        window.alert('考试已结束，无法进入')
        return
      }
      if (Number.isFinite(start) && now < start) {
        this.examWaitContext = item
        this.startExamWaitTimer()
        return
      }
      this.activeExam = item
      this.currentQuestionIndex = 0
      this.showResumeTip = false
      this.restoredQuestionIndex = null
      this.answers = {}
      this.markedQuestions = {}
      this.dirtyQuestionIds = {}
      this.codingRunByQuestion = {}
      this.codingRunLoadingId = null
      this.pyodideRuntimeWarmStarted = false
      this.loadPaperQuestionsForExam(item.exam.paperId, item.record.id)
      this.startExamTimers(Number.isFinite(end) ? end : null, item.record.id)
      this.examHandRemoteStatus = 'NONE'
      this.examDmMessages = []
      this.examDmLastId = 0
      this.examDmInput = ''
      this.stopExamHandPoll()
      this.stopExamDmPoll()
      this.$nextTick(() => {
        this.startExamCameraMonitor(false)
        this.startExamCameraHealthCheck()
        this.startExamAntiCheat()
        this.startExamMonitorUpload()
        this.startExamBroadcastPoll()
        this.startExamVoicePoll()
        this.startExamHandPoll()
      })
    },
    async ensureSingleScreenBeforeExam(item) {
      if (!this.enableSecondScreenCheck) return true
      this.pendingEnterExamItem = null
      const recordId = item && item.record ? item.record.id : null
      const inExam = !!(this.activeExam && this.activeExam.record)
      const ctxTip = inExam ? '继续考试' : '进入考试'

      if (!window.isSecureContext) {
        this.secondScreenCount = 1
        this.showSecondScreenModal = true
        window.alert(`副屏检测需要 HTTPS 或 localhost 安全环境；当前环境不满足，暂无法${ctxTip}。`)
        return false
      }
      if (typeof window.getScreenDetails !== 'function') {
        this.secondScreenCount = 1
        this.showSecondScreenModal = true
        window.alert(`当前浏览器不支持副屏检测接口，暂无法${ctxTip}。请使用最新版 Chrome/Edge 并以 HTTPS 或 localhost 访问。`)
        return false
      }

      try {
        const details = await window.getScreenDetails()
        const screens = (details && details.screens) || []
        const count = Math.max(1, Array.isArray(screens) ? screens.length : 1)
        this.secondScreenCount = count
        this.secondScreenDetails = details
        if (count > 1) {
          this.showSecondScreenModal = true
          this.pendingEnterExamItem = inExam ? null : item
          if (!inExam && recordId != null) {
            fetch('/api/student/exam/violation', {
              method: 'POST',
              headers: this.authHeaders({ 'Content-Type': 'application/json' }),
              body: JSON.stringify({
                recordId,
                userId: this.user && this.user.id,
                type: 'MULTI_SCREEN',
                detail: `screens=${count}`
              })
            }).catch(() => {})
          }
          return false
        }
        this.showSecondScreenModal = false
        return true
      } catch (e) {
        this.secondScreenCount = 1
        this.showSecondScreenModal = true
        const msg = (e && e.name) || ''
        window.alert(`副屏检测失败（${msg || '未授权或接口异常'}），请允许“窗口管理/屏幕枚举”权限后再试。`)
        return false
      }
    },
    async recheckSecondScreen() {
      if (!this.enableSecondScreenCheck) {
        this.showSecondScreenModal = false
        this.pendingEnterExamItem = null
        return
      }
      if (this.secondScreenChecking) return
      this.secondScreenChecking = true
      try {
        const pending = this.pendingEnterExamItem
        const ok = await this.ensureSingleScreenBeforeExam(pending || this.activeExam)
        if (ok && pending) {
          this.pendingEnterExamItem = null
          this.doEnterExamProceed(pending)
        }
      } finally {
        this.secondScreenChecking = false
      }
    },
    async startSecondScreenGuard() {
      if (!this.enableSecondScreenCheck) return
      this.stopSecondScreenGuard()
      this.secondScreenGuardToken += 1
      const token = this.secondScreenGuardToken
      await this.ensureSingleScreenBeforeExam(this.activeExam).catch(() => {})
      if (token !== this.secondScreenGuardToken) return
      this.secondScreenPollTimer = setInterval(() => {
        if (!this.activeExam) return
        if (document.hidden || document.visibilityState !== 'visible') return
        this.ensureSingleScreenBeforeExam(this.activeExam).catch(() => {})
      }, 4000)
      const details = this.secondScreenDetails
      if (details && typeof details.addEventListener === 'function') {
        this.boundSecondScreenChange = () => {
          if (!this.activeExam) return
          this.ensureSingleScreenBeforeExam(this.activeExam).catch(() => {})
        }
        details.addEventListener('screenschange', this.boundSecondScreenChange)
      }
    },
    stopSecondScreenGuard() {
      this.secondScreenGuardToken += 1
      if (this.secondScreenPollTimer) clearInterval(this.secondScreenPollTimer)
      this.secondScreenPollTimer = null
      const details = this.secondScreenDetails
      if (details && this.boundSecondScreenChange && typeof details.removeEventListener === 'function') {
        details.removeEventListener('screenschange', this.boundSecondScreenChange)
      }
      this.boundSecondScreenChange = null
      this.secondScreenDetails = null
      this.pendingEnterExamItem = null
      this.showSecondScreenModal = false
      this.secondScreenChecking = false
    },
    startExamAntiCheat() {
      this.stopExamAntiCheat()
      this.ignoreScreenUntil = Date.now() + 2800
      this.lastScreenViolMs = 0
      this.lastBehaviorReportMs = 0
      this.screenCheatNeedAck = false
      this.screenCheatPendingForce = false
      this.screenForceSubmitting = false
      this.showScreenAckModal = false
      this.showScreenForceModal = false
      this.screenAckInput = ''
      this.boundAntiVis = () => this.onExamVisibilityChange()
      this.boundAntiKey = (e) => this.onExamKeyDown(e)
      this.boundAntiCtx = (e) => this.onExamContextMenu(e)
      this.boundAntiPaste = (e) => this.onExamPaste(e)
      document.addEventListener('visibilitychange', this.boundAntiVis)
      document.addEventListener('keydown', this.boundAntiKey, true)
      document.addEventListener('contextmenu', this.boundAntiCtx, true)
      document.addEventListener('paste', this.boundAntiPaste, true)
      this.startSecondScreenGuard()
    },
    stopExamAntiCheat() {
      if (this.boundAntiVis) {
        document.removeEventListener('visibilitychange', this.boundAntiVis)
      }
      if (this.boundAntiKey) {
        document.removeEventListener('keydown', this.boundAntiKey, true)
      }
      if (this.boundAntiCtx) {
        document.removeEventListener('contextmenu', this.boundAntiCtx, true)
      }
      if (this.boundAntiPaste) {
        document.removeEventListener('paste', this.boundAntiPaste, true)
      }
      this.boundAntiVis = null
      this.boundAntiKey = null
      this.boundAntiCtx = null
      this.boundAntiPaste = null
      this.showScreenAckModal = false
      this.showScreenForceModal = false
      this.screenCheatNeedAck = false
      this.screenCheatPendingForce = false
      this.stopSecondScreenGuard()
    },
    onExamKeyDown(e) {
      if (!this.activeExam || !this.activeExam.record) return
      const k = String(e.key || '').toLowerCase()
      if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x'].includes(k)) {
        e.preventDefault()
        e.stopPropagation()
        const tag = e.target && e.target.tagName ? String(e.target.tagName).toUpperCase() : ''
        this.reportBehaviorViolation('KEYBOARD_' + String(e.key).toUpperCase(), tag)
      }
    },
    onExamContextMenu(e) {
      if (!this.activeExam || !this.activeExam.record) return
      e.preventDefault()
      e.stopPropagation()
      this.reportBehaviorViolation('CONTEXT_MENU', 'right_click')
    },
    onExamPaste(e) {
      if (!this.activeExam || !this.activeExam.record) return
      e.preventDefault()
      e.stopPropagation()
      this.reportBehaviorViolation('PASTE_BLOCK', 'paste_event')
    },
    reportBehaviorViolation(type, detail) {
      if (!this.activeExam || !this.activeExam.record || !this.user.id) return
      if (this.imageLightboxUrl) return
      if (this.isExamAnticheatMuted()) return
      const now = Date.now()
      if (now - this.lastBehaviorReportMs < 400) return
      this.lastBehaviorReportMs = now
      fetch('/api/student/exam/violation', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recordId: this.activeExam.record.id,
          userId: this.user.id,
          type: String(type || 'BEHAVIOR').slice(0, 80),
          detail: String(detail || '').slice(0, 400)
        })
      }).catch(() => {})
    },
    onExamVisibilityChange() {
      if (!this.activeExam) return
      if (this.imageLightboxUrl) return
      if (this.isExamAnticheatMuted()) return
      if (document.visibilityState === 'hidden') {
        if (Date.now() < this.ignoreScreenUntil) return
        this.reportScreenViolation('visibility_hidden')
      } else {
        if (this.screenCheatPendingForce) {
          this.showScreenForceModal = true
        } else if (this.screenCheatNeedAck) {
          this.showScreenAckModal = true
          this.screenAckInput = ''
        }
      }
    },
    reportScreenViolation(detailSrc) {
      if (!this.activeExam || !this.activeExam.record || !this.user.id) return
      if (this.imageLightboxUrl) return
      if (this.isExamAnticheatMuted()) return
      const now = Date.now()
      if (now - this.lastScreenViolMs < 700) return
      this.lastScreenViolMs = now
      const payload = {
        recordId: this.activeExam.record.id,
        userId: this.user.id,
        type: 'SCREEN_SWITCH',
        detail: String(detailSrc || 'switch').slice(0, 400)
      }
      fetch('/api/student/exam/violation', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) return
          const n = (res.data && res.data.screenCount) != null ? Number(res.data.screenCount) : 0
          if (this.activeExam && this.activeExam.record) {
            this.activeExam.record.screenCount = n
          }
          if (n >= this.screenCheatLimit) {
            this.screenCheatPendingForce = true
            this.screenCheatNeedAck = false
            this.showScreenAckModal = false
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
              this.showScreenForceModal = true
            }
            this.$nextTick(() => {
              if (this.activeExam && this.activeExam.record && Number(this.activeExam.record.id) === Number(payload.recordId)) {
                this.confirmForceSubmitByScreen()
              }
            })
          } else {
            this.screenCheatNeedAck = true
            this.screenCheatAckCount = n
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
              this.showScreenAckModal = true
              this.screenAckInput = ''
            }
          }
        })
        .catch(() => {})
    },
    confirmScreenAck() {
      if (String(this.screenAckInput || '').trim() !== '我已知晓') {
        window.alert('请完整输入「我已知晓」后确认。')
        return
      }
      this.showScreenAckModal = false
      this.screenCheatNeedAck = false
      this.screenAckInput = ''
    },
    confirmForceSubmitByScreen() {
      if (!this.activeExam) return
      if (this.screenForceSubmitting || this.examSubmitInProgress) return
      this.screenForceSubmitting = true
      const rid = this.activeExam.record.id
      this.showScreenForceModal = false
      this.stopExamAntiCheat()
      this.submitExam(rid, 'FORCE_END', true, 'screen')
    },
    loadPaperQuestionsForExam(paperId, recordId) {
      const rid = recordId != null ? encodeURIComponent(recordId) : ''
      fetch(`/api/student/exam/paper/${paperId}/questions?recordId=${rid}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          const list = (res && res.data && Array.isArray(res.data)) ? res.data : []
          this.paperQuestions = list
          this.questionMap = {}
          list.forEach((q) => {
            if (q && q.id != null) this.questionMap[q.id] = q
          })
          this.examQuestions = list
            .slice()
            .map((q) => (q ? { ...q, title: this.normalizeStemText(q.title), analysis: this.normalizeStemText(q.analysis) } : q))
            .sort((a, b) => (a.sort || 0) - (b.sort || 0))
          this.loadSavedAnswers(recordId)
        })
    },
    loadSavedAnswers(recordId) {
      fetch(`/api/crud/exam-answer?page=0&size=1000&recordId=${recordId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          const list = (res.data && res.data.content) || []
          list.forEach((a) => {
            const qid = this.normalizeQuestionId(a.questionId)
            if (qid == null) return
            this.setAnswerForQuestion(qid, a.userAnswer || '')
          })
          return list.length > 0
        })
        .catch(() => false)
        .then((hasServerAnswers) => {
          this.restoreLocalAnswerSnapshot(recordId)
          const hasAnyAnswers =
            hasServerAnswers ||
            Object.keys(this.answers).some((k) => !!String(this.answers[k] || '').trim())
          this.restoreExamProgress(recordId, hasAnyAnswers)
          this.applyCodingTemplatesIfEmpty()
          this.ensureExamPythonRuntimeOnce()
          if (this.pendingScratchQuestionIndex != null && this.examQuestions && this.examQuestions.length) {
            const mx = this.examQuestions.length - 1
            let idx = Number(this.pendingScratchQuestionIndex) || 0
            if (idx < 0) idx = 0
            if (idx > mx) idx = mx
            this.currentQuestionIndex = idx
            this.pendingScratchQuestionIndex = null
          }
          this.maybeApplyScratchMergeFromPage()
        })
    },
    examProgressKey(recordId) {
      return `exam_progress_${recordId}`
    },
    examAnswersSnapshotKey(recordId) {
      return `exam_answers_${recordId}`
    },
    saveExamProgress(recordId) {
      try {
        localStorage.setItem(this.examProgressKey(recordId), JSON.stringify({
          questionIndex: this.currentQuestionIndex,
          savedAt: Date.now()
        }))
      } catch (e) {
        void e
      }
    },
    saveLocalAnswerSnapshot(recordId) {
      if (!recordId) return
      try {
        localStorage.setItem(this.examAnswersSnapshotKey(recordId), JSON.stringify({
          answers: this.answers || {},
          savedAt: Date.now()
        }))
      } catch (e) {
        void e
      }
    },
    restoreLocalAnswerSnapshot(recordId) {
      if (!recordId) return
      try {
        const raw = localStorage.getItem(this.examAnswersSnapshotKey(recordId))
        if (!raw) return
        const data = JSON.parse(raw || '{}')
        const localAnswers = data && data.answers && typeof data.answers === 'object' ? data.answers : {}
        Object.keys(localAnswers).forEach((rawQid) => {
          const qid = this.normalizeQuestionId(rawQid)
          if (qid == null) return
          const v = localAnswers[rawQid]
          if (v == null) return
          const serverVal = String(this.getAnswerForQuestion(qid) || '').trim()
          const localVal = String(v)
          // 服务端已有作答时不覆盖：Scratch 上传以服务器为准，避免本地快照把别的题工程写回去
          if (serverVal) {
            const q = (this.examQuestions || []).find((x) => this.normalizeQuestionId(x.id) === qid)
            if (q && String(q.type || '').toLowerCase() === SCRATCH_TYPE) {
              return
            }
            if (serverVal === localVal) return
            // 非 Scratch：仅当本地与服务器不同时保留服务器，不把过期本地标脏回写
            return
          }
          if (!String(localVal || '').trim()) return
          this.setAnswerForQuestion(qid, localVal)
          this.dirtyQuestionIds[qid] = true
        })
      } catch (e) {
        void e
      }
    },
    restoreExamProgress(recordId, hasSavedAnswers) {
      try {
        const raw = localStorage.getItem(this.examProgressKey(recordId))
        if (!raw) return
        const data = JSON.parse(raw || '{}')
        const idx = Number(data.questionIndex || 0)
        if (idx >= 0 && idx < this.examQuestions.length) {
          this.currentQuestionIndex = idx
          this.restoredQuestionIndex = idx
          if (this.restoreTipTimer) clearTimeout(this.restoreTipTimer)
          this.restoreTipTimer = setTimeout(() => {
            this.restoredQuestionIndex = null
          }, 3500)
        }
        if (hasSavedAnswers || idx > 0) {
          this.showResumeTip = true
        }
      } catch (e) {
        void e
      }
    },
    scrollCurrentQuestionIntoView() {
      this.$nextTick(() => {
        const refs = this.$refs.qNavBtns
        const btn = Array.isArray(refs) ? refs[this.currentQuestionIndex] : refs
        if (btn && btn.scrollIntoView) {
          btn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
        }
      })
    },
    startExamTimers(endTs, recordId) {
      this.clearExamTimers()
      this.autosaveTimer = setInterval(() => this.autoSaveAll(recordId), 8000)
      if (endTs == null || !Number.isFinite(endTs)) {
        this.countdownSeconds = 0
        return
      }
      const refresh = () => {
        const left = Math.max(0, Math.floor((endTs - Date.now()) / 1000))
        this.countdownSeconds = left
        if (left <= 0) {
          this.forceSubmit(recordId)
        }
      }
      refresh()
      this.countdownTimer = setInterval(refresh, 1000)
    },
    examQuestionTypeLabel(type) {
      const s = String(type || '').toLowerCase()
      if (s === 'radio') return '单选题'
      if (s === 'multiple') return '多选题'
      if (s === 'coding') return '编程题'
      if (s === MATERIAL_COLLECT_TYPE) return '资料收集题'
      if (s === SCRATCH_TYPE) return 'Scratch编程题'
      if (s === 'fill' || s === 'blank') return '填空题'
      if (s === 'essay' || s === 'subjective') return '主观题'
      return type ? String(type) : '—'
    },
    materialMaxCount(q) {
      return parseMaxAttachments(q)
    },
    materialText(qid) {
      return parseMaterialAnswer(this.answers[qid]).text
    },
    setMaterialText(qid, text) {
      const d = parseMaterialAnswer(this.answers[qid])
      d.text = text
      this.answers[qid] = stringifyMaterialAnswer(d)
      this.markDirty(qid)
    },
    materialAttachments(qid) {
      return parseMaterialAnswer(this.answers[qid]).attachments
    },
    materialFileSizeLabel(size) {
      const n = Number(size || 0)
      if (!Number.isFinite(n) || n <= 0) return '-'
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
      return `${(n / 1024 / 1024).toFixed(1)} MB`
    },
    onMaterialFileChange(ev, q) {
      const input = ev && ev.target
      const file = input && input.files && input.files[0]
      if (input) input.value = ''
      if (!file || !q || !this.activeExam || !this.activeExam.record) return
      const err = validateMaterialFile(file)
      if (err) {
        window.alert(err)
        return
      }
      const max = parseMaxAttachments(q)
      const cur = parseMaterialAnswer(this.answers[q.id]).attachments.length
      if (cur >= max) {
        window.alert(`本题最多上传 ${max} 个附件，无法继续添加`)
        return
      }
      const fd = new FormData()
      fd.append('file', file)
      fd.append('recordId', String(this.activeExam.record.id))
      fd.append('questionId', String(q.id))
      fetch('/api/student/exam/material-asset/upload', {
        method: 'POST',
        headers: this.authHeaders(),
        body: fd
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200 || !res.data) {
            window.alert((res && res.message) || '上传失败')
            return
          }
          const d = parseMaterialAnswer(this.answers[q.id])
          d.attachments.push({
            storageName: res.data.storageName,
            originalName: res.data.originalName,
            size: res.data.size,
            downloadUrl: res.data.downloadUrl,
            viewUrl: res.data.viewUrl
          })
          this.answers[q.id] = stringifyMaterialAnswer(d)
          this.markDirty(q.id)
        })
        .catch(() => window.alert('上传失败，请检查网络后重试'))
    },
    removeMaterialAttachment(qid, idx) {
      const d = parseMaterialAnswer(this.answers[qid])
      if (!d.attachments || idx < 0 || idx >= d.attachments.length) return
      d.attachments.splice(idx, 1)
      this.answers[qid] = stringifyMaterialAnswer(d)
      this.markDirty(qid)
    },
    validateAllMaterialBeforeSubmit() {
      const list = this.examQuestions || []
      for (let i = 0; i < list.length; i++) {
        const q = list[i]
        if (String(q.type || '').toLowerCase() !== MATERIAL_COLLECT_TYPE) continue
        if (!isMaterialAnswerComplete(this.answers[q.id], q)) {
          window.alert(materialSubmitBlockedMessage(parseMaxAttachments(q)))
          return false
        }
      }
      return true
    },
    validateAllScratchBeforeSubmit() {
      const list = this.examQuestions || []
      for (let i = 0; i < list.length; i++) {
        const q = list[i]
        if (String(q.type || '').toLowerCase() !== SCRATCH_TYPE) continue
        if (!isScratchAnswerComplete(this.answers[q.id])) {
          window.alert(scratchSubmitBlockedMessage())
          return false
        }
      }
      return true
    },
    onScratchUploaded(qid, data) {
      const id = this.normalizeQuestionId(qid)
      if (id == null || !data) return
      const prev = parseScratchAnswer(this.getAnswerForQuestion(id))
      const merged = stringifyScratchAnswer({
        ...prev,
        storageName: data.storageName,
        originalName: data.originalName,
        size: data.size,
        downloadUrl: data.downloadUrl,
        viewUrl: data.viewUrl,
        publicProjectUrl: data.publicProjectUrl || ''
      })
      this.setAnswerForQuestion(id, merged)
      // 上传接口已写入 exam_answer，本地只需同步展示，勿标脏以免 autosave 用旧快照回写
      const rid = this.activeExam && this.activeExam.record && this.activeExam.record.id
      if (rid) this.saveLocalAnswerSnapshot(rid)
    },
    scratchUploadLabel(qid) {
      const a = parseScratchAnswer(this.answers[qid])
      if (!a.storageName) return ''
      return `已上传：${a.originalName || 'project.sb3'}`
    },
    codingIoSpec(question) {
      const pub = question && question.codingPublic
      if (pub && pub.ioSpec) return String(pub.ioSpec)
      return ''
    },
    applyCodingTemplatesIfEmpty() {
      (this.examQuestions || []).forEach((q) => {
        if (String(q.type || '').toLowerCase() !== 'coding') return
        const qid = this.normalizeQuestionId(q.id)
        if (qid == null) return
        const existing = this.getAnswerForQuestion(qid).trim()
        if (existing) return
        const tpl = q.codingPublic && q.codingPublic.template
        if (tpl && String(tpl).trim()) {
          this.setAnswerForQuestion(qid, String(tpl))
        }
      })
    },
    codingOutputText(questionId) {
      const qid = this.normalizeQuestionId(questionId)
      const r = qid != null ? this.codingRunByQuestion[qid] : null
      if (!r) return '点击「运行代码」查看程序输出'
      if (r.error) return r.error
      const out = String(r.stdout || '')
      const err = String(r.stderr || '')
      if (!out && !err) return '（程序结束，无输出）'
      if (!err) return out
      if (!out) return '[stderr]\n' + err
      return out + '\n\n[stderr]\n' + err
    },
    /** 统一题号键，避免 answers[1] 与 answers["1"] 混用导致多道编程题串题 */
    normalizeQuestionId(id) {
      if (id == null || id === '') return null
      const n = Number(id)
      return Number.isFinite(n) ? n : null
    },
    getAnswerForQuestion(questionId) {
      const qid = this.normalizeQuestionId(questionId)
      if (qid == null) return ''
      return String(this.answers[qid] ?? '')
    },
    setAnswerForQuestion(questionId, value) {
      const qid = this.normalizeQuestionId(questionId)
      if (qid == null) return
      this.answers[qid] = value == null ? '' : String(value)
    },
    async flushCodingDraftForQuestion(questionId) {
      const rid = this.activeExam && this.activeExam.record && this.activeExam.record.id
      const qid = this.normalizeQuestionId(questionId)
      if (!rid || qid == null || !this.dirtyQuestionIds[qid]) return
      const code = this.getAnswerForQuestion(qid)
      try {
        await this.saveCodingDraftOnly(rid, qid, code)
        const next = { ...this.dirtyQuestionIds }
        delete next[qid]
        this.dirtyQuestionIds = next
      } catch (e) {
        void e
      }
    },
    async saveStudentPythonCodeOnly() {
      const q = this.currentQuestion
      if (!q || String(q.type || '').toLowerCase() !== 'coding') return
      if (!this.activeExam || !this.activeExam.record) return
      const qid = this.normalizeQuestionId(q.id)
      if (qid == null) return
      const code = this.getAnswerForQuestion(qid)
      const rid = this.activeExam.record.id
      try {
        await this.saveCodingDraftOnly(rid, qid, code)
        const next = { ...this.dirtyQuestionIds }
        delete next[qid]
        this.dirtyQuestionIds = next
        this.saveLocalAnswerSnapshot(rid)
        window.alert('代码已保存')
      } catch (e) {
        window.alert((e && e.message) || '保存失败')
      }
    },
    ensureExamPythonRuntimeOnce() {
      if (this.pyodideRuntimeWarmStarted) return
      const hasCoding = (this.examQuestions || []).some(
        (q) => String(q.type || '').toLowerCase() === 'coding'
      )
      if (!hasCoding) return
      this.pyodideRuntimeWarmStarted = true
      preloadPyodide().catch(() => {})
    },
    async runStudentPythonCode() {
      const q = this.currentQuestion
      if (!q || String(q.type || '').toLowerCase() !== 'coding') return
      if (!this.activeExam || !this.activeExam.record) return
      const qid = this.normalizeQuestionId(q.id)
      if (qid == null) return
      const code = this.getAnswerForQuestion(qid).trim()
      if (!code) {
        window.alert('请先输入代码')
        return
      }
      this.setAnswerForQuestion(qid, code)
      const rid = this.activeExam.record.id
      this.codingRunLoadingId = qid
      try {
        await this.saveCodingDraftOnly(rid, qid, code)
        const next = { ...this.dirtyQuestionIds }
        delete next[qid]
        this.dirtyQuestionIds = next
        this.saveLocalAnswerSnapshot(rid)
      } catch (e) {
        window.alert((e && e.message) || '保存失败，请检查网络后重试')
        this.codingRunLoadingId = null
        return
      }
      try {
        const result = await this.requestStudentPythonRun(code, qid)
        this.codingRunByQuestion = {
          ...this.codingRunByQuestion,
          [qid]: result
        }
      } catch (e) {
        this.codingRunByQuestion = {
          ...this.codingRunByQuestion,
          [qid]: { error: (e && e.message) || '运行失败', stdout: '', stderr: '' }
        }
      } finally {
        if (this.codingRunLoadingId === qid) this.codingRunLoadingId = null
      }
    },
    isPythonEnvPreparingMessage(msg) {
      const s = String(msg || '')
      return (s.includes('Python') && s.includes('稍后')) || s.includes('环境准备') || s.includes('运行繁忙')
    },
    formatPythonRunResult(payload) {
      if (!payload) return { error: '运行失败', stdout: '', stderr: '' }
      if (payload.error) {
        return {
          error: String(payload.error),
          stdout: payload.stdout != null ? String(payload.stdout) : '',
          stderr: payload.stderr != null ? String(payload.stderr) : ''
        }
      }
      return {
        error: '',
        stdout: payload.stdout != null ? String(payload.stdout) : '',
        stderr: payload.stderr != null ? String(payload.stderr) : ''
      }
    },
    async requestStudentPythonRun(code, qid) {
      this.codingRunByQuestion = {
        ...this.codingRunByQuestion,
        [qid]: {
          error: '',
          stdout: '正在加载/运行 Python（首次约 10～30 秒，请稍候）…',
          stderr: ''
        }
      }
      const pyResult = await runPythonInBrowser(code, { timeoutMs: 10000 })
      if (!pyResult.error) {
        return this.formatPythonRunResult(pyResult)
      }

      this.codingRunByQuestion = {
        ...this.codingRunByQuestion,
        [qid]: {
          error: '',
          stdout: '浏览器 Python 不可用，正在尝试服务器运行…',
          stderr: String(pyResult.error || '')
        }
      }
      return this.requestStudentPythonRunOnServer(code, qid, 0)
    },
    async requestStudentPythonRunOnServer(code, qid, attempt) {
      const maxAttempts = 3
      let res
      try {
        const r = await fetch('/api/student/coding/run', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code })
        })
        if (!r.ok) {
          const msg = r.status === 502 || r.status === 503
            ? '服务器暂时不可用，请稍后重试'
            : `运行失败（HTTP ${r.status}）`
          if (this.isPythonEnvPreparingMessage(msg) && attempt < maxAttempts) {
            this.codingRunByQuestion = {
              ...this.codingRunByQuestion,
              [qid]: {
                error: '',
                stdout: `服务器 Python 准备中（${attempt + 1}/${maxAttempts}）…`,
                stderr: ''
              }
            }
            await new Promise((resolve) => setTimeout(resolve, 2000))
            return this.requestStudentPythonRunOnServer(code, qid, attempt + 1)
          }
          return {
            error: msg,
            stdout: '',
            stderr: ''
          }
        }
        res = await r.json()
      } catch (e) {
        if (attempt < maxAttempts) {
          this.codingRunByQuestion = {
            ...this.codingRunByQuestion,
            [qid]: {
              error: '',
              stdout: `网络异常，正在重试（${attempt + 1}/${maxAttempts}）…`,
              stderr: ''
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 2000))
          return this.requestStudentPythonRunOnServer(code, qid, attempt + 1)
        }
        return { error: '网络错误，请检查连接后重试', stdout: '', stderr: '' }
      }

      if (!res || res.code !== 200) {
        const msg = String(res && res.message ? res.message : '运行失败')
        if (this.isPythonEnvPreparingMessage(msg) && attempt < maxAttempts) {
          this.codingRunByQuestion = {
            ...this.codingRunByQuestion,
            [qid]: {
              error: '',
              stdout: `服务器 Python 准备中（${attempt + 1}/${maxAttempts}）…`,
              stderr: ''
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 2000))
          return this.requestStudentPythonRunOnServer(code, qid, attempt + 1)
        }
        return {
          error: this.isPythonEnvPreparingMessage(msg)
            ? '浏览器与服务器 Python 均暂不可用，请检查网络后重试'
            : msg || '运行失败',
          stdout: '',
          stderr: ''
        }
      }

      const d = res.data || {}
      return this.formatPythonRunResult({
        stdout: d.stdout,
        stderr: d.stderr,
        error: ''
      })
    },
    onCodingTextareaKeydown(e, questionId) {
      // Tab 键默认会把焦点跳走；拦截后插入缩进字符用于编程题代码输入
      if (!e || e.key !== 'Tab') return
      if (!questionId && questionId !== 0) return
      const textarea = e.target
      if (!textarea || typeof textarea.selectionStart !== 'number') return

      e.preventDefault()

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const value = String(this.answers[questionId] || '')
      const insert = '\t'

      const next = value.slice(0, start) + insert + value.slice(end)
      this.answers[questionId] = next
      this.markDirty(questionId)

      // 等 v-model 把值写回 textarea 后再恢复光标位置
      this.$nextTick(() => {
        try {
          const el = textarea
          const pos = start + insert.length
          el.selectionStart = pos
          el.selectionEnd = pos
        } catch (err) {
          // ignore
        }
      })
    },
    selectSingleChoice(questionId, key) {
      this.persistChoiceAnswer(questionId, key)
    },
    /** 选择题/多选题：写入本地并立即保存草稿，避免跳转 Scratch 后返回丢失 */
    async persistChoiceAnswer(questionId, userAnswer) {
      const qid = this.normalizeQuestionId(questionId)
      if (qid == null) return
      this.setAnswerForQuestion(qid, userAnswer == null ? '' : String(userAnswer))
      this.markDirty(qid)
      const recordId = this.activeExam && this.activeExam.record && this.activeExam.record.id
      if (!recordId) return
      try {
        await this.saveDraftForQuestion(recordId, qid, this.getAnswerForQuestion(qid))
        const next = { ...this.dirtyQuestionIds }
        delete next[qid]
        this.dirtyQuestionIds = next
      } catch (e) {
        void e
      }
    },
    optionEntries(question) {
      try {
        const obj = question && question.options ? JSON.parse(question.options) : {}
        return Object.keys(obj).map((k) => ({
          key: k,
          value: obj[k],
          imageUrl: this.normalizeAssetUrl(resolveOptionImage(question, k))
        }))
      } catch (e) {
        return []
      }
    },
    stemImageList(question) {
      if (!question) return []
      return parseUrlList(question.contentImages)
        .map((u) => this.normalizeAssetUrl(u))
        .filter(Boolean)
    },
    /** 与教师端录入一致：保留换行/缩进；解码 Excel/Unicode/HTML 实体，避免引号、-> 等显示成编码 */
    normalizeStemText(text) {
      let s = String(text == null ? '' : text)
      // Excel OOXML：_x0040_ → @，_x000A_ → 换行
      s = s.replace(/_x([0-9A-Fa-f]{4})_/g, (_, hex) => {
        const code = parseInt(hex, 16)
        return Number.isFinite(code) ? String.fromCharCode(code) : _
      })
      // 字面量 \u0040
      s = s.replace(/\\u([0-9A-Fa-f]{4})/g, (_, hex) => {
        const code = parseInt(hex, 16)
        return Number.isFinite(code) ? String.fromCharCode(code) : _
      })
      // HTML 实体 &#64; / &#x40;
      s = s.replace(/&#(x?[0-9A-Fa-f]+);/gi, (full, num) => {
        try {
          const code = String(num).toLowerCase().startsWith('x')
            ? parseInt(String(num).slice(1), 16)
            : parseInt(String(num), 10)
          return Number.isFinite(code) ? String.fromCodePoint(code) : full
        } catch (e) {
          return full
        }
      })
      // 命名实体（历史 API htmlEscape 残留；&amp; 最后解以免二次实体）
      const named = {
        lsquo: '\u2018',
        rsquo: '\u2019',
        ldquo: '\u201C',
        rdquo: '\u201D',
        quot: '"',
        apos: "'",
        lt: '<',
        gt: '>',
        nbsp: '\u00A0',
        ndash: '\u2013',
        mdash: '\u2014',
        hellip: '\u2026'
      }
      s = s.replace(/&([a-zA-Z]+);/g, (full, name) => {
        const key = String(name || '').toLowerCase()
        if (key === 'amp') return full
        return Object.prototype.hasOwnProperty.call(named, key) ? named[key] : full
      })
      s = s.replace(/&amp;/g, '&')
      s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
      if (!s.includes('\n') && /\\n/.test(s)) {
        s = s.replace(/\\n/g, '\n').replace(/\\t/g, '\t')
      }
      return s
    },
    analysisImageList(question) {
      if (!question) return []
      return parseUrlList(question.analysisImages)
        .map((u) => this.normalizeAssetUrl(u))
        .filter(Boolean)
    },
    openImageLightbox(url) {
      const u = this.normalizeAssetUrl(url)
      if (!u) return
      this.imageLightboxScale = 1.2
      this.imageLightboxUrl = u
      this.examAnticheatMutedUntil = Math.max(this.examAnticheatMutedUntil, Date.now() + 120000)
    },
    closeImageLightbox() {
      this.imageLightboxUrl = null
      this.imageLightboxScale = 1
    },
    onImageLightboxWheel(e) {
      if (!this.imageLightboxUrl) return
      const dy = e.deltaY || 0
      const step = dy > 0 ? -0.12 : 0.12
      this.imageLightboxScale = Math.min(4, Math.max(0.25, this.imageLightboxScale + step))
    },
    isMultiChecked(questionId, key) {
      const v = this.getAnswerForQuestion(questionId)
      return v.split(',').map((x) => x.trim()).includes(key)
    },
    toggleMulti(questionId, key) {
      const arr = this.getAnswerForQuestion(questionId)
        .split(',')
        .map((x) => x.trim())
        .filter((x) => x)
      const set = new Set(arr)
      if (set.has(key)) set.delete(key)
      else set.add(key)
      this.persistChoiceAnswer(questionId, Array.from(set).sort().join(','))
    },
    markDirty(questionId) {
      const qid = this.normalizeQuestionId(questionId)
      if (qid == null) return
      this.dirtyQuestionIds[qid] = true
      const recordId = this.activeExam && this.activeExam.record && this.activeExam.record.id
      if (recordId) this.saveLocalAnswerSnapshot(recordId)
    },
    toggleMark(questionId) {
      this.markedQuestions[questionId] = !this.markedQuestions[questionId]
    },
    isAnswered(questionId) {
      const qid = this.normalizeQuestionId(questionId)
      const q = (this.examQuestions || []).find((x) => this.normalizeQuestionId(x.id) === qid)
      const raw = this.getAnswerForQuestion(qid != null ? qid : questionId)
      if (q && String(q.type || '').toLowerCase() === MATERIAL_COLLECT_TYPE) {
        return isMaterialAnswerComplete(raw, q)
      }
      if (q && String(q.type || '').toLowerCase() === SCRATCH_TYPE) {
        return isScratchAnswerComplete(raw)
      }
      return !!String(raw || '').trim()
    },
    autoSaveAll(recordId) {
      this.saveExamProgress(recordId)
      this.saveLocalAnswerSnapshot(recordId)
      const ids = Object.keys(this.dirtyQuestionIds)
      if (ids.length === 0) return
      this.saveDraftBatch(recordId, ids)
    },
    saveDraftBatch(recordId, questionIds) {
      const answers = questionIds.map((qid) => ({
        questionId: Number(qid),
        userAnswer: this.getAnswerForQuestion(qid)
      }))
      return fetch('/api/student/exam/draft-batch', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recordId: Number(recordId),
          answers
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) {
            throw new Error((res && res.message) || '批量保存失败')
          }
          const saved = (res.data && res.data.savedQuestionIds) || []
          const next = { ...this.dirtyQuestionIds }
          saved.forEach((qid) => {
            delete next[String(qid)]
          })
          this.dirtyQuestionIds = next
          return res
        })
    },
    async flushDirtyAnswersToServer(recordId) {
      const ids = Object.keys(this.dirtyQuestionIds)
      if (!recordId || ids.length === 0) return
      try {
        await this.saveDraftBatch(recordId, ids)
      } catch (e) {
        void e
      }
      this.saveLocalAnswerSnapshot(recordId)
    },
    isCodingQuestionId(questionId) {
      const q = (this.examQuestions || []).find((x) => String(x.id) === String(questionId))
      return !!(q && String(q.type || '').toLowerCase() === 'coding')
    },
    /** 编程题专用：单接口单题写入，不触发整卷提交逻辑 */
    saveCodingDraftOnly(recordId, questionId, userAnswer) {
      const qid = this.normalizeQuestionId(questionId)
      if (qid == null) {
        return Promise.reject(new Error('题目编号无效'))
      }
      return fetch('/api/student/exam/draft-coding', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recordId: Number(recordId),
          questionId: qid,
          userAnswer: userAnswer == null ? '' : String(userAnswer)
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) {
            throw new Error((res && res.message) || '保存编程代码失败')
          }
          return res
        })
    },
    saveDraftForQuestion(recordId, questionId, userAnswer) {
      if (this.isCodingQuestionId(questionId)) {
        return this.saveCodingDraftOnly(recordId, questionId, userAnswer)
      }
      return this.saveAnswer(recordId, questionId, userAnswer)
    },
    saveAnswer(recordId, questionId, userAnswer) {
      const payload = { recordId, questionId, userAnswer: userAnswer || '', score: 0, isCorrect: 0 }
      const headers = this.authHeaders({ 'Content-Type': 'application/json' })
      return fetch(
        `/api/crud/exam-answer?page=0&size=1&recordId=${recordId}&questionId=${questionId}`,
        { headers: this.authHeaders() }
      )
        .then((r) => r.json())
        .then((res) => {
          if (!res || res.code !== 200) {
            throw new Error((res && res.message) || '查询已有作答失败')
          }
          const list = (res.data && res.data.content) || []
          if (list.length > 0) {
            const id = list[0].id
            return fetch(`/api/crud/exam-answer/${id}`, {
              method: 'PUT',
              headers,
              body: JSON.stringify({ ...list[0], ...payload })
            }).then((r) => r.json())
          }
          return fetch('/api/crud/exam-answer', {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
          }).then((r) => r.json())
        })
        .then((res) => {
          if (!res || res.code !== 200) {
            throw new Error((res && res.message) || '保存答案失败')
          }
          return res
        })
    },
    submitExamConfirm() {
      if (!this.activeExam || this.examSubmitInProgress) return
      this.clearSubmitConfirmTimer()
      this.submitConfirmSecondsLeft = 10
      this.showSubmitConfirmModal = true
      this.submitConfirmTimer = setInterval(() => {
        if (this.submitConfirmSecondsLeft <= 1) {
          this.submitConfirmSecondsLeft = 0
          this.clearSubmitConfirmTimer()
        } else {
          this.submitConfirmSecondsLeft -= 1
        }
      }, 1000)
    },
    clearSubmitConfirmTimer() {
      if (this.submitConfirmTimer) {
        clearInterval(this.submitConfirmTimer)
        this.submitConfirmTimer = null
      }
    },
    closeSubmitConfirmModal() {
      this.clearSubmitConfirmTimer()
      this.showSubmitConfirmModal = false
      this.submitConfirmSecondsLeft = 0
    },
    confirmSubmitExamFinal() {
      if (!this.activeExam || this.submitConfirmSecondsLeft > 0 || this.examSubmitInProgress) return
      this.submitExam(this.activeExam.record.id, 'COMMIT', false, '')
    },
    exitExamSession(recordId) {
      try { localStorage.removeItem(this.examProgressKey(recordId)) } catch (e) { void e }
      try { localStorage.removeItem(this.examAnswersSnapshotKey(recordId)) } catch (e) { void e }
      this.stopExamAntiCheat()
      this.stopExamBroadcastPoll()
      this.closeExamBroadcastModal(true)
      this.stopExamVoicePoll()
      this.stopExamHandPoll()
      this.stopExamDmPoll()
      const ex = this.activeExam
      if (ex && ex.record && ex.exam && (this.examVoicePc || this.examVoiceLocalStream)) {
        fetch('/api/student/exam-voice/signal', {
          method: 'POST',
          headers: this.authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            examId: ex.exam.id,
            recordId: ex.record.id,
            signalType: 'hangup',
            payload: '{}'
          })
        }).catch(() => {})
      }
      this.teardownExamVoice()
      this.clearExamTimers()
      this.stopExamCameraMonitor()
      this.activeExam = null
      this.examHandRemoteStatus = 'NONE'
      this.examDmMessages = []
      this.examDmLastId = 0
      this.examQuestions = []
      this.answers = {}
      this.markedQuestions = {}
      this.dirtyQuestionIds = {}
      this.currentTab = 'home'
      this.restoredQuestionIndex = null
    },
    showSubmitResultAlert(silent, forceReason, screenLimit) {
      if (!silent) {
        window.alert('交卷成功')
      } else if (forceReason === 'screen') {
        window.alert(`因切屏/退出全屏累计达 ${screenLimit} 次，系统已强制交卷`)
      } else {
        window.alert('考试时间到，已强制交卷')
      }
    },
    forceSubmit(recordId) {
      this.submitExam(recordId, 'FORCE_END', true, '')
    },
    isSubmittedExamStatus(status) {
      const st = String(status || '').trim().toUpperCase()
      return st === 'COMMIT' || st === 'FORCE_END' || st === 'GRADED'
    },
    async submitExam(recordId, status, silent = false, forceReason = '') {
      if (this.examSubmitInProgress) return
      const forceEnd = String(status || '').toUpperCase() === 'FORCE_END'
      if (!forceEnd && !this.validateAllMaterialBeforeSubmit()) {
        return
      }
      if (!forceEnd && !this.validateAllScratchBeforeSubmit()) {
        return
      }
      this.examSubmitInProgress = true
      this.closeSubmitConfirmModal()
      const screenLimit = this.screenCheatLimit
      try {
        await this.flushDirtyAnswersToServer(recordId)
      } catch (e) {
        void e
      }
      const allAnswers = this.examQuestions.map((q) => ({
        questionId: q.id,
        userAnswer: this.getAnswerForQuestion(q.id)
      }))
      // 先请求交卷接口，确认成功后再提示/退出；避免“假成功”导致查看答案失败
      fetch('/api/student/exam/submit', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recordId,
          status,
          answers: allAnswers
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert((res.message || '交卷提交未确认成功') + '，请联系监考老师确认。')
            return
          }
          this.exitExamSession(recordId)
          this.showSubmitResultAlert(silent, forceReason, screenLimit)
          this.loadExamData()
          const allowReview = res.data && Number(res.data.showAnswerAfterSubmit) === 1
          const allowRedo = res.data && Number(res.data.allowRedo) === 1
          if (allowReview) {
            this.openAnswerReview(recordId, 'home')
          } else if (allowRedo) {
            if (window.confirm('本场考试允许重做。是否立即清空作答并重新答题？')) {
              this.redoExam(recordId, true)
            }
          }
        })
        .catch((e) => {
          window.alert((e.message || '交卷提交失败') + '，请联系监考老师确认。')
        })
        .finally(() => {
          this.examSubmitInProgress = false
          this.screenForceSubmitting = false
        })
    },
    redoExam(recordId, skipConfirm = false) {
      if (!recordId || this.examSubmitInProgress) return
      if (!skipConfirm && !window.confirm('确定重做本场试卷吗？\n将清空本次已作答内容与成绩，需重新答题并提交。')) {
        return
      }
      this.examSubmitInProgress = true
      fetch(`/api/student/exam/redo/${recordId}`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' })
      })
        .then((r) => r.json())
        .then(async (res) => {
          if (res.code !== 200 || !res.data) {
            window.alert((res && res.message) || '重做失败')
            return
          }
          try {
            localStorage.removeItem(this.examProgressKey(recordId))
            localStorage.removeItem(this.examAnswersSnapshotKey(recordId))
          } catch (e) {
            void e
          }
          this.answers = {}
          this.markedQuestions = {}
          this.dirtyQuestionIds = {}
          this.answerReview = {
            loading: false,
            showAnswerAfterSubmit: 0,
            allowRedo: 0,
            examName: '',
            recordId: null,
            message: '',
            questions: [],
            returnTab: 'home'
          }
          await this.loadExamData()
          this.loadMyScores()
          const record = (this.examRecords || []).find((r) => String(r.id) === String(recordId))
          const examId = (res.data && res.data.examId) || (record && record.examId)
          const exam = (this.exams || []).find((e) => String(e.id) === String(examId))
          if (!record || !exam) {
            window.alert('已清空作答。请返回首页刷新后点击「进入考试」重新作答。')
            this.currentTab = 'home'
            return
          }
          this.currentTab = 'exam'
          this.openPrecheck({ record, exam })
        })
        .catch((e) => {
          window.alert((e && e.message) || '重做失败')
        })
        .finally(() => {
          this.examSubmitInProgress = false
        })
    },
    openAnswerReview(recordId, returnTab = 'score') {
      if (!recordId) return
      this.answerReview = {
        loading: true,
        showAnswerAfterSubmit: 0,
        allowRedo: 0,
        examName: '',
        recordId,
        message: '',
        questions: [],
        returnTab: returnTab || 'score'
      }
      this.currentTab = 'answerReview'
      fetch(`/api/student/exam/review/${recordId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200 || !res.data) {
            this.answerReview.loading = false
            this.answerReview.message = (res && res.message) || '加载答案失败'
            this.answerReview.showAnswerAfterSubmit = 0
            this.answerReview.allowRedo = 0
            this.answerReview.questions = []
            return
          }
          const d = res.data
          this.answerReview.loading = false
          this.answerReview.examName = d.examName || ''
          this.answerReview.showAnswerAfterSubmit = Number(d.showAnswerAfterSubmit) === 1 ? 1 : 0
          this.answerReview.allowRedo = Number(d.allowRedo) === 1 ? 1 : 0
          this.answerReview.message = d.message || ''
          this.answerReview.questions = Array.isArray(d.questions) ? d.questions : []
        })
        .catch(() => {
          this.answerReview.loading = false
          this.answerReview.message = '加载答案失败'
          this.answerReview.showAnswerAfterSubmit = 0
          this.answerReview.allowRedo = 0
          this.answerReview.questions = []
        })
    },
    closeAnswerReview() {
      const tab = this.answerReview && this.answerReview.returnTab ? this.answerReview.returnTab : 'home'
      this.currentTab = tab
      if (tab === 'score') this.loadMyScores()
    },
    isChoiceReviewType(type) {
      const t = String(type || '').toLowerCase()
      return t === 'radio' || t === 'single' || t === 'multiple' || t === 'judge'
    },
    reviewOptionEntries(row) {
      return optionEntriesFromRow(row || {})
    },
    parseReviewAnswerKeys(text) {
      const set = new Set()
      String(text == null ? '' : text)
        .split(/[,，\s]+/)
        .forEach((x) => {
          const v = String(x || '').trim().toUpperCase()
          if (v) set.add(v)
        })
      return set
    },
    reviewOptionMarkClass(row, optKey) {
      const key = String(optKey || '').trim().toUpperCase()
      if (!key) return ''
      const correct = this.parseReviewAnswerKeys(row && row.correctAnswer)
      const user = this.parseReviewAnswerKeys(row && row.userAnswer)
      if (correct.has(key)) return 'review-opt-correct'
      if (user.has(key) && !correct.has(key)) return 'review-opt-wrong'
      return ''
    },
    displayReviewUserAnswer(q) {
      if (!q) return '—'
      if (q.materialCollectText != null && String(q.materialCollectText).trim() !== '') {
        return String(q.materialCollectText)
      }
      const ua = q.userAnswer
      if (ua == null || String(ua).trim() === '') return '未作答'
      return String(ua)
    },
    logout() {
      this.clearExamTimers()
      if (this.restoreTipTimer) clearTimeout(this.restoreTipTimer)
      this.stopCamera()
      this.stopExamCameraMonitor()
      this.stopExamAntiCheat()
      this.stopExamBroadcastPoll()
      this.closeExamBroadcastModal(true)
      this.stopExamVoicePoll()
      this.teardownExamVoice()
      this.stopExamHandPoll()
      this.stopExamDmPoll()
      this.clearSubmitConfirmTimer()
      this.showSubmitConfirmModal = false
      localStorage.removeItem('exam_user')
      window.location.hash = '#/'
    }
  }
}
</script>

<style scoped>
.role-page {
  position: relative;
  min-height: 100vh;
  padding: 20px;
  color: #dce9ff;
  background:
    radial-gradient(circle at 8% 10%, rgba(75, 133, 255, 0.26), transparent 34%),
    radial-gradient(circle at 90% 18%, rgba(52, 190, 255, 0.2), transparent 30%),
    linear-gradient(150deg, #081734, #0c2450 48%, #0a1b3f);
}

/* 正式考试中：隐藏侧栏，主区域铺满视口，减少干扰 */
.role-page.role-page-exam-full {
  padding: 0;
  align-items: stretch;
  min-height: 100vh;
}

.role-page-exam-full .content-area-exam-full {
  flex: 1 1 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
}

.role-page-exam-full .exam-secure-root {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.role-page-exam-full .exam-secure-card {
  flex: 1 1 auto;
  margin-bottom: 0;
  border-radius: 0;
  border-left: none;
  border-right: none;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 14px 18px 20px;
}

.role-page-exam-full .exam-layout {
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr) minmax(220px, 280px);
  gap: 12px;
  min-height: calc(100vh - 200px);
  align-items: start;
}

.role-page-exam-full .exam-left-rail,
.role-page-exam-full .exam-nav-card {
  max-height: calc(100vh - 160px);
}

.role-page-exam-full .exam-layout--automan .exam-left-rail--proctor-only {
  max-height: none;
  height: auto;
}

.side-nav {
  width: 240px;
  min-height: calc(100vh - 40px);
  margin-right: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(9, 28, 64, 0.88);
  border: 1px solid rgba(132, 169, 245, 0.38);
  border-radius: 12px;
  box-shadow: 0 18px 40px rgba(7, 18, 46, 0.45);
}

.nav-btn {
  border: 1px solid rgba(138, 174, 243, 0.42);
  border-radius: 8px;
  padding: 10px 12px;
  color: #d9e8ff;
  background: rgba(17, 51, 106, 0.74);
  text-align: left;
}

.nav-btn.active {
  background: linear-gradient(120deg, #2f6feb, #5da9ff);
  color: #ffffff;
  border-color: transparent;
}

.exam-list-hint {
  line-height: 1.55;
  color: rgba(210, 226, 255, 0.78);
}

.content-area {
  flex: 1;
}

.card-box {
  background: rgba(11, 33, 76, 0.8);
  border: 1px solid rgba(138, 174, 243, 0.35);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 14px 32px rgba(7, 18, 46, 0.35);
}

.notice-board-card {
  min-height: 220px;
}

.notice-board-heading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.notice-board-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.35);
  flex-shrink: 0;
}

.notice-title-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.notice-item-unread-dot {
  width: 7px;
  height: 7px;
  margin-top: 6px;
  border-radius: 50%;
  background: #dc3545;
  flex-shrink: 0;
}

.notice-board-list {
  max-height: 240px;
  overflow: auto;
}

.notice-item {
  border: 1px solid rgba(138, 174, 243, 0.35);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: rgba(255, 255, 255, 0.9);
}

.notice-clickable {
  cursor: pointer;
}

.notice-clickable:hover {
  border-color: rgba(47, 111, 235, 0.55);
}

.notice-title {
  font-weight: 700;
  color: #1f2a44;
}

.notice-meta {
  font-size: 12px;
  color: #5c7098;
  margin: 4px 0 6px;
}

.notice-content {
  color: #1f2a44;
  white-space: pre-wrap;
}

.notice-modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(8, 24, 56, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.notice-modal-panel {
  width: min(1080px, calc(100vw - 24px));
  max-height: 88vh;
  overflow: auto;
  border-radius: 16px;
  border: 1px solid #d8e6ff;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.96), rgba(244, 250, 255, 0.98));
  color: #1f2a44;
  padding: 16px;
}

.notice-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #d8e6ff;
  margin-bottom: 12px;
}

.notice-modal-section {
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  padding: 12px;
  background: #f9fcff;
  margin-bottom: 12px;
}

.notice-layout {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 12px;
}

.notice-main-section,
.notice-side-section {
  margin-bottom: 0;
}

.notice-section-title {
  font-weight: 700;
  color: #2b4f8f;
  margin-bottom: 8px;
}

.notice-modal-text {
  white-space: pre-wrap;
  line-height: 1.6;
  color: #1f2a44;
  max-height: 58vh;
  overflow: auto;
}

.notice-asset-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  max-height: 58vh;
  overflow: auto;
}

.notice-asset-item {
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  padding: 12px;
  background: #ffffff;
}

.notice-asset-name {
  font-weight: 700;
  color: #1f2a44;
  word-break: break-all;
}

.notice-asset-actions {
  display: flex;
  gap: 8px;
}

.notice-asset-image {
  width: 100%;
  max-height: 180px;
  object-fit: contain;
  border: 1px solid #d8e6ff;
  border-radius: 8px;
}

@media (max-width: 992px) {
  .notice-layout {
    grid-template-columns: 1fr;
  }

  .notice-modal-text,
  .notice-asset-list {
    max-height: none;
  }
}

.precheck-step {
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  padding: 12px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
  position: relative;
}

.precheck-step.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.step-success-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(47, 111, 235, 0.18);
  border: 1px solid rgba(47, 111, 235, 0.5);
  font-size: 12px;
  font-weight: 700;
}

.precheck-shell {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}

.exam-entry-strong,
.exam-entry-strong .small,
.exam-entry-strong p,
.exam-entry-strong span,
.exam-entry-strong td,
.exam-entry-strong th,
.exam-entry-strong label,
.exam-entry-strong .question-title,
.exam-entry-strong .step-title,
.exam-entry-strong .text-muted {
  color: #1f2a44 !important;
  font-weight: 700 !important;
}

.exam-entry-strong .precheck-emphasis {
  color: #dc2626 !important;
  font-weight: 800 !important;
}

.camera-box {
  width: 100%;
  height: 220px;
  border-radius: 10px;
  background: #0b2046;
  border: 1px solid rgba(138, 174, 243, 0.45);
  object-fit: cover;
}

.photo-preview {
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(138, 174, 243, 0.45);
}

.photo-placeholder {
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px dashed rgba(138, 174, 243, 0.55);
}

.status-ok {
  color: #7dd3fc;
  font-weight: 700;
}

.status-fail {
  color: #ffb4a8;
  font-weight: 700;
}

.exam-secure-root {
  outline: none;
}

.exam-secure-card {
  user-select: none;
}

.exam-exam-top-bar {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 2.5rem;
}

.exam-exam-top-title {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 42%;
  margin: 0;
  padding-right: 8px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exam-countdown-line {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: max-content;
  max-width: min(96%, 56vw);
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.4;
  color: inherit;
  white-space: nowrap;
  pointer-events: none;
}

.exam-countdown-time {
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.exam-entry-strong .exam-countdown-time {
  color: #dc3545 !important;
  font-weight: 800 !important;
}

.exam-secure-card textarea,
.exam-secure-card input[type='text'],
.exam-secure-card input[type='radio'],
.exam-secure-card input[type='checkbox'] {
  user-select: text;
}

/* 防作弊说明（右栏底部）：主次两行、整体略放大 */
.exam-secure-card .exam-anti-inline-red {
  line-height: 1.45 !important;
  color: #dc3545 !important;
  max-width: 100%;
}

.exam-secure-card .exam-nav-card-foot.exam-anti-inline-red {
  text-align: left !important;
  font-size: 0.875rem !important;
}

.exam-secure-card .exam-nav-card-foot .exam-anti-line--lead {
  font-size: 1rem !important;
  font-weight: 800 !important;
  color: #c82333 !important;
  margin-bottom: 6px;
}

.exam-secure-card .exam-nav-card-foot .exam-anti-line--detail {
  font-size: 0.9375rem !important;
  font-weight: 600 !important;
  color: #dc3545 !important;
}

.exam-secure-card .exam-anti-inline-red strong {
  color: #9b1c2e !important;
  font-weight: 800 !important;
}

.exam-submit-final-warn,
.modal-mask.exam-submit-confirm-mask .exam-submit-final-warn {
  color: #dc3545 !important;
}

.modal-mask.exam-submit-confirm-mask {
  z-index: 4800;
}

.modal-mask.exam-submit-confirm-mask .exam-submit-confirm-panel {
  max-width: 520px;
}

.exam-submit-countdown-hint {
  margin-bottom: 1rem;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111111 !important;
  line-height: 1.5;
}

.exam-submit-countdown-hint strong {
  color: #111111 !important;
  font-weight: 800 !important;
}

.exam-layout {
  display: grid;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr) minmax(220px, 280px);
  gap: 10px;
  align-items: start;
}

.exam-left-rail {
  border: 1px solid #d8e6ff;
  border-radius: 10px;
  padding: 8px;
  max-height: 72vh;
  overflow: auto;
  overflow-x: hidden;
  background: rgba(255, 255, 255, 0.96);
  color: #1f2a44;
  box-shadow: 0 8px 20px rgba(18, 45, 94, 0.12);
  min-width: 0;
  box-sizing: border-box;
}

/* Automan：无摄像头时左侧做成紧凑卡片，勿强行拉高留白 */
.exam-layout--automan .exam-left-rail--proctor-only {
  height: auto;
  min-height: 0;
  max-height: none;
  padding: 14px 14px 16px;
  align-self: start;
}

.exam-layout--automan .exam-left-rail--proctor-only .exam-hand-panel {
  margin: 0;
}

.exam-layout--automan .exam-left-rail--proctor-only .exam-hand-body {
  margin-bottom: 12px !important;
  line-height: 1.55;
  font-size: 0.875rem;
}

.exam-layout--automan .exam-left-rail--proctor-only .exam-side-btn {
  min-height: 40px;
}

.exam-center-col {
  min-width: 0;
  min-height: 280px;
}

.coding-exam-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
  min-height: 320px;
}

@media (max-width: 768px) {
  .coding-exam-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.coding-exam-editor,
.coding-exam-output {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.coding-exam-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 36px;
  margin-bottom: 6px;
}

.coding-exam-panel-head--output {
  justify-content: flex-start;
}

.coding-mini-pre {
  margin: 0;
  padding: 6px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.coding-exam-output-pre {
  flex: 1 1 auto;
  min-height: 260px;
  margin: 0;
  padding: 10px 12px;
  font-size: 0.8125rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background: #0f172a;
  color: #e2e8f0;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  overflow: auto;
}

.exam-center-empty {
  min-height: 360px;
}

.exam-right-rail {
  min-width: 0;
}

.exam-nav-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #d8e6ff;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  color: #1f2a44;
  box-shadow: 0 8px 20px rgba(18, 45, 94, 0.12);
  max-height: 68vh;
  overflow: hidden;
}

.exam-nav-card-body {
  padding: 12px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.exam-nav-card-heading {
  font-size: 13px;
  font-weight: 800;
  color: #29539b;
  margin-bottom: 8px;
}

.exam-nav-card-foot {
  flex-shrink: 0;
  padding: 10px 12px;
  border-top: 1px solid rgba(220, 53, 69, 0.28);
  background: rgba(255, 252, 252, 0.95);
}

.exam-nav-card .q-grid {
  grid-template-columns: repeat(4, 1fr);
}

.camera-monitor {
  border: 1px solid #d8e6ff;
  border-radius: 8px;
  padding: 8px;
  background: #f7fbff;
}

.monitor-title {
  color: #29539b;
  font-weight: 700;
  margin-bottom: 6px;
}

/* 考试中左栏：紧凑宽度，状态文案可换行完整展示 */
.exam-left-rail .exam-side-block-title {
  font-size: 0.9375rem !important;
  font-weight: 800 !important;
  color: #15345f !important;
  letter-spacing: 0.02em;
  margin-bottom: 6px;
  line-height: 1.35;
  word-break: break-word;
}

.exam-left-rail .camera-monitor {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.exam-left-rail .monitor-video {
  flex: 0 0 96px;
}

.exam-left-rail .exam-monitor-status-line {
  margin-top: 6px;
  font-size: 0.875rem !important;
  line-height: 1.45 !important;
  font-weight: 700 !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
}

.exam-left-rail .exam-monitor-status-line.monitor-ok {
  color: #0d4ed1 !important;
}

.exam-left-rail .exam-monitor-status-line.monitor-fail {
  color: #c82333 !important;
}

.exam-left-rail .exam-hand-body {
  font-size: 0.9375rem !important;
  line-height: 1.55 !important;
  font-weight: 500 !important;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.exam-left-rail .exam-hand-body--muted {
  color: #4a5568 !important;
}

.exam-left-rail .exam-hand-body--info {
  color: #0b5cab !important;
  font-weight: 600 !important;
}

.exam-left-rail .exam-hand-body--success {
  color: #0b5d36 !important;
  font-weight: 600 !important;
}

.exam-left-rail .exam-side-btn {
  font-size: 0.875rem !important;
  font-weight: 700 !important;
  padding: 0.35rem 0.5rem !important;
}

.exam-left-rail .exam-side-btn--compact {
  font-size: 0.9375rem !important;
  padding: 0.4rem 0.55rem !important;
}

.exam-left-rail .exam-dm-log--sized {
  font-size: 0.875rem !important;
}

.exam-left-rail .exam-dm-input {
  font-size: 0.9375rem !important;
}

.monitor-video {
  width: 100%;
  max-width: 100%;
  height: 96px;
  object-fit: cover;
  border-radius: 6px;
  background: #0b2046;
  border: 1px solid #bfd3f7;
}

.exam-hand-panel {
  border: 1px solid #d8e6ff;
  border-radius: 8px;
  padding: 8px;
  background: #f7fbff;
}

/* 小屏：左右栏接近等宽，把宽度让给中间答题区 */
@media (max-width: 1280px) {
  .exam-layout {
    grid-template-columns: minmax(200px, 236px) minmax(0, 1fr) minmax(200px, 248px);
    gap: 8px;
  }

  .role-page-exam-full .exam-layout {
    grid-template-columns: minmax(200px, 236px) minmax(0, 1fr) minmax(200px, 248px);
    gap: 8px;
  }

  .exam-left-rail .monitor-video,
  .monitor-video {
    height: 88px;
  }

  .exam-left-rail .monitor-video {
    flex-basis: 88px;
  }
}

.exam-dm-log {
  max-height: 140px;
  overflow-y: auto;
  padding: 6px;
  border-radius: 6px;
  background: rgba(11, 32, 70, 0.06);
  border: 1px solid #e0e9fb;
}

.exam-dm-line + .exam-dm-line {
  margin-top: 4px;
}

.q-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.q-no-btn {
  border: 1px solid #b7ccf7;
  background: #ffffff;
  border-radius: 8px;
  padding: 6px 0;
  font-weight: 700;
  color: #1f2a44;
}

/* 当前题但未完成：保持白底，仅用描边标示题号位置 */
.q-no-btn.active:not(.answered) {
  background: #ffffff !important;
  color: #2f6feb !important;
  border: 2px solid #2f6feb !important;
  box-shadow: 0 0 0 1px rgba(47, 111, 235, 0.2);
}

/* 已完成：整格蓝色底、题号白色（未完成不加此类，样式不变） */
.q-no-btn.answered {
  background: #2f6feb !important;
  color: #ffffff !important;
  border: 1px solid #2563eb !important;
}

.q-no-btn.active.answered {
  border: 2px solid #1d4ed8 !important;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.q-no-btn.marked:not(.answered) {
  border-color: #ffb300;
}

.question-panel {
  background: rgba(255, 255, 255, 0.96);
  color: #1f2a44;
  border-radius: 10px;
  padding: 12px;
  border: 1px solid #d8e6ff;
}

.exam-single-wrap .exam-choice-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.exam-q-img-thumb {
  display: block;
  max-width: min(100%, 600px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #c5d6f5;
  background: #fff;
  cursor: zoom-in;
}

.exam-img-lightbox-hint {
  color: #ffffff !important;
  opacity: 1;
  font-weight: 500;
}

.exam-img-zoom-viewport {
  overflow: auto;
  max-height: min(88vh, 960px);
  min-height: min(52vh, 480px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 10px;
  background: rgba(8, 16, 36, 0.65);
}

.exam-q-img-lightbox {
  display: block;
  max-width: min(96vw, 1400px);
  max-height: min(84vh, 900px);
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto;
  border-radius: 10px;
  background: #0b1224;
  transition: transform 0.08s ease-out;
}

.modal-mask.exam-img-lightbox-mask {
  position: fixed;
  inset: 0;
  background: rgba(6, 14, 34, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 6000;
  padding: 16px;
}

.exam-img-lightbox-panel {
  width: min(96vw, 1440px);
  max-width: calc(100vw - 24px);
  background: rgba(12, 26, 54, 0.95);
  border: 1px solid rgba(148, 179, 246, 0.5);
  border-radius: 14px;
  padding: 12px 14px 14px;
  color: #ffffff;
}

.exam-analysis-box {
  background: #f6f9ff;
  border: 1px dashed #bcd0f5;
}

.exam-analysis-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.exam-choice-btn--rich {
  flex-direction: column;
  align-items: stretch;
}

.exam-choice-main {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.exam-choice-img {
  display: block;
  margin-top: 6px;
}

.exam-choice-btn {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid #c5d6f5;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fbfdff;
  color: #1f2a44;
  font-size: 0.95rem;
  line-height: 1.45;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.exam-choice-btn:hover {
  border-color: #7aa8ff;
  background: #f0f6ff;
}

.exam-choice-btn.active {
  border-color: #2f6feb;
  background: linear-gradient(120deg, #e8f1ff, #f5f9ff);
  box-shadow: 0 0 0 2px rgba(47, 111, 235, 0.2);
}

.review-opt.review-opt-correct,
.review-opt.review-opt-correct .exam-choice-key,
.review-opt.review-opt-correct .exam-choice-text {
  background: #d4edda;
  color: #155724;
  border-color: #a3d7b0;
}

.review-opt.review-opt-wrong,
.review-opt.review-opt-wrong .exam-choice-key,
.review-opt.review-opt-wrong .exam-choice-text {
  background: #f8d7da;
  color: #721c24;
  border-color: #f1b0b7;
}

.answer-review-item {
  padding: 14px 16px;
  border: 1px solid rgba(138, 174, 243, 0.35);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
}

.answer-review-user-ans {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.95rem;
  color: inherit;
  margin: 0;
}

.answer-review-correct,
.answer-review-analysis {
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
}

.question-title {
  font-weight: 800;
}

.question-stem-block {
  line-height: 1.55;
}

.question-stem-label {
  font-family: "SimHei", "Heiti SC", "Microsoft YaHei", "PingFang SC", sans-serif;
  font-weight: 800 !important;
  color: #1e4d8f !important;
  margin-bottom: 6px;
}

/* 各类题干统一：黑体 + 蓝色背景框 + 保留换行缩进 */
.question-stem-content {
  font-family: "SimHei", "Heiti SC", "Microsoft YaHei", "PingFang SC", sans-serif !important;
  font-weight: 600 !important;
  font-size: 1rem;
  line-height: 1.55;
  color: #1f2a44 !important;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  tab-size: 4;
  background: rgba(241, 247, 255, 0.9);
  border: 1px solid rgba(145, 180, 240, 0.45);
  border-radius: 10px;
  padding: 10px 12px;
}

.coding-io-spec-body {
  white-space: pre-wrap;
  word-break: break-word;
  margin-top: 4px;
}

.modal-mask.exam-anti-modal {
  position: fixed;
  inset: 0;
  background: rgba(3, 13, 35, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-mask.exam-anti-modal .modal-panel {
  width: 520px;
  max-width: calc(100vw - 24px);
  padding: 18px;
  border-radius: 12px;
  background: rgba(10, 31, 72, 0.98);
  border: 1px solid rgba(138, 174, 243, 0.45);
  color: #e8f1ff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
}

.modal-mask.exam-broadcast-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
  padding: 16px;
}

.exam-broadcast-panel {
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: min(70vh, 520px);
  overflow: auto;
  padding: 20px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #c7d7f5;
  color: #1f2a44;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
}

.exam-broadcast-body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
  font-size: 15px;
}

.exam-wait-shell {
  max-width: 720px;
  margin: 0 auto;
}

.exam-wait-countdown {
  text-align: center;
  padding: 20px 16px;
  border-radius: 12px;
  background: linear-gradient(165deg, #f0f6ff, #e8f2ff);
  border: 1px solid #c5d6f5;
}

.exam-wait-countdown-digits {
  font-size: 2.25rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  color: #1d4ed8;
}
</style>

